"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks, SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";
import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { TprCoverageContinueButton } from "@/configuration/groups/tpr/components/coverage-continue-button";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { isTprSpecialCaseInsurance } from "@/configuration/groups/tpr/insurance-special-cases";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import type { StediEligibilityDisplayLabel, TprSyncChecklistTraceEntry } from "@/configuration/groups/tpr/workflows/sync-checklist";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { TaskSchedulingRecommendation } from "@/src/components/tasks/task-scheduling-recommendation";
import { VerificationButtons } from "@/src/components/tasks/verification-buttons";
import { ValerieCard } from "@/src/components/valerie-card";
import { tprReferralAuthNeedsPendingUpload, tprReferralAuthNeedsReview } from "@/src/lib/customer/tpr/referral-auth-pending-upload";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput, TprReferralAuthorizationOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, getCoverageDisplayName } from "@/src/models/coverage";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { getPatientDisplayName, getPatientFaxNumber, getPatientName, getPatientPhoneNumber, type Patient } from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskStatus, TaskType } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
const NOT_AVAILABLE = "N/A";

// Plain string literals rather than importing the `TprSyncBucket` enum's runtime values: this is a
// "use client" component, and `sync-checklist.ts` transitively pulls in server-only Athena/GCP client
// code, so any real (non-type-only) import from it breaks the browser bundle. Values must match
// `TprSyncBucket` in configuration/groups/tpr/workflows/sync-checklist.ts exactly.
const TPR_MANUAL_REVIEW_BUCKET = "biltmorecardreferral";

/**
 * Plain-English labels for the 3 real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  closed: "Closed — upcoming appointment already scheduled",
  sscardio: "SS Cardio — stays with practice",
  biltmorecardiosched: "Biltmore — ready to schedule"
};
const NO_CONFIDENT_BUCKET_SUGGESTION = "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = provider && getPractitionerNameWithCredentials(provider) || NOT_AVAILABLE;
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const npi = provider?.npiNumber || NOT_AVAILABLE;
  let t2;
  if ($[2] !== name) {
    t2 = <CopyableContent text={name} />;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== npi) {
    t3 = <CopyableContent text={npi} />;
    $[4] = npi;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>Name: {t2}, NPI: {t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ResolvedProviderCopyFields(t0) {
  const $ = _c(8);
  const {
    providerLabel,
    providerNpi,
    writeSafe
  } = t0;
  const t1 = providerLabel || NOT_AVAILABLE;
  let t2;
  if ($[0] !== t1) {
    t2 = <CopyableContent text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = providerNpi || NOT_AVAILABLE;
  let t4;
  if ($[2] !== t3) {
    t4 = <CopyableContent text={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !writeSafe && " \u2014 verify manually before entry";
  let t6;
  if ($[4] !== t2 || $[5] !== t4 || $[6] !== t5) {
    t6 = <>Name: {t2}, NPI:{" "}{t4}{t5}</>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;
  const display = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: true
  });
  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;
  return `${date} - ${type} (${status})`;
}
function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;
    return bTime - aTime;
  });
}
type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(label: string, referralValue: string | null, chartValue: string | null): DataSourceEntry {
  if (referralValue) return {
    label,
    value: referralValue,
    source: "Referral",
    missing: false
  };
  if (chartValue) return {
    label,
    value: chartValue,
    source: "Patient Chart",
    missing: false
  };
  return {
    label,
    value: NOT_AVAILABLE,
    source: "N/A",
    missing: true
  };
}
type TimelineStatus = "automated" | "verified" | "verify" | "needs_action" | "failed" | "waiting" | "not_run" | "not_needed";
type TimelineStep = {
  number: number;
  title: string;
  status: TimelineStatus;
  summary: string;
  taskStepKeys: StepKey[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control: React.ReactNode;
  };
};
export type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};
type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientPhoneNumber: string;
  patientFaxNumber: string;
  patientEthnicity: string;
  patientRace: string;
  patientLanguage: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  referralDate: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  /** PIMC / Triwest / MercyCare — special-case payers ops wants visually flagged on Insurance. */
  isSpecialCaseInsurance: boolean;
  diagnosis: string;
};
export function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;
type ProviderSelectionStep = NonNullable<ProviderSelectionEvidence["selectedStep"]>;
function providerSelectionReason(selectedStep: ProviderSelectionEvidence["selectedStep"]): string {
  switch (selectedStep) {
    case "upcoming_appointment":
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case "past_appointment":
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case "usual_provider":
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case "referral_provider":
      return "Selected because this provider is named on the referral.";
    case "diagnosis_match":
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}
function ProviderSelectionChecklist(t0) {
  const $ = _c(35);
  const {
    evidence,
    upcomingAppointmentProviderSummary,
    referralProviderName,
    referralProviderNpi,
    diagnosis,
    selectedProviderName,
    selectedLocation
  } = t0;
  let t1;
  if ($[0] !== evidence.appointmentProviders) {
    t1 = evidence.appointmentProviders.some(_temp2);
    $[0] = evidence.appointmentProviders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasQualificationEvidence = t1;
  let t2;
  if ($[2] !== evidence.appointmentProviders || $[3] !== evidence.selectedStep || $[4] !== hasQualificationEvidence || $[5] !== upcomingAppointmentProviderSummary) {
    t2 = period => {
      const providers = evidence.appointmentProviders.flatMap(provider_0 => {
        const count = period === "past" ? provider_0.qualifyingPastAppointmentCount ?? 0 : provider_0.qualifyingUpcomingAppointmentCount ?? 0;
        const locationEhrId = period === "past" ? provider_0.mostRecentPastLocationEhrId : provider_0.earliestUpcomingLocationEhrId;
        const location = locationEhrId ? getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}` : null;
        return count > 0 ? [`${provider_0.providerName}${provider_0.providerNpi ? ` · ${provider_0.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`] : [];
      });
      if (providers.length > 0) {
        return providers.join(", ");
      }
      if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
        return upcomingAppointmentProviderSummary;
      }
      if (!hasQualificationEvidence && (evidence.selectedStep === "usual_provider" || evidence.appointmentProviders.length > 0)) {
        return "Not captured for this legacy task";
      }
      return "None";
    };
    $[2] = evidence.appointmentProviders;
    $[3] = evidence.selectedStep;
    $[4] = hasQualificationEvidence;
    $[5] = upcomingAppointmentProviderSummary;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const providerCountSummary = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== diagnosis || $[8] !== evidence.pastAppointmentCount || $[9] !== evidence.selectedStep || $[10] !== evidence.upcomingAppointmentCount || $[11] !== providerCountSummary || $[12] !== referralProviderName || $[13] !== referralProviderNpi || $[14] !== selectedLocation || $[15] !== selectedProviderName) {
    const steps = [{
      key: "upcoming_appointment",
      title: "Check for an upcoming appointment",
      items: [{
        label: "Upcoming within 60 days",
        value: evidence.upcomingAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("upcoming")
      }]
    }, {
      key: "past_appointment",
      title: "Check past appointments at Biltmore locations",
      items: [{
        label: "Completed in past 3 years",
        value: evidence.pastAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("past")
      }]
    }, {
      key: "referral_provider",
      title: "Use the provider named on the referral",
      items: [{
        label: "Provider",
        value: referralProviderName
      }, {
        label: "NPI",
        value: referralProviderNpi
      }]
    }, {
      key: "diagnosis_match",
      title: "Sanity-check the pick or find a replacement",
      items: [{
        label: "Diagnosis",
        value: diagnosis
      }, {
        label: "Location",
        value: selectedLocation
      }, {
        label: "Final provider",
        value: selectedProviderName
      }]
    }];
    t6 = "space-y-2 border-t pt-3";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    t5 = "mt-2 space-y-2";
    t3 = "space-y-2";
    t4 = steps.map((step, index) => {
      const selected = evidence.selectedStep === step.key;
      return <li key={step.key} className={selected ? "space-y-2 rounded-md border border-success/40 bg-success/10 p-3" : "space-y-2 rounded-md border bg-muted/10 p-3"}><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">Step {index + 1} — {step.title}</span>{selected ? <Badge variant="outline" className="border-success/40 bg-success/10 text-success">Selected</Badge> : null}</div><DecisionMetadata items={selected ? [...step.items, {
          label: "Picked provider",
          value: selectedProviderName
        }, {
          label: "Reason",
          value: providerSelectionReason(evidence.selectedStep)
        }] : step.items} /></li>;
    });
    $[7] = diagnosis;
    $[8] = evidence.pastAppointmentCount;
    $[9] = evidence.selectedStep;
    $[10] = evidence.upcomingAppointmentCount;
    $[11] = providerCountSummary;
    $[12] = referralProviderName;
    $[13] = referralProviderNpi;
    $[14] = selectedLocation;
    $[15] = selectedProviderName;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t4) {
    t8 = <ol className={t3}>{t4}</ol>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== evidence.selectedStep) {
    t9 = evidence.selectedStep === "usual_provider" ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">This assignment was created with the retired Athena usual-provider rule. Verify the provider and location against qualifying appointment history before following it.</div> : null;
    $[25] = evidence.selectedStep;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t5 || $[28] !== t8 || $[29] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t6 || $[33] !== t7) {
    t11 = <details className={t6}>{t7}{t10}</details>;
    $[31] = t10;
    $[32] = t6;
    $[33] = t7;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}
function _temp2(provider) {
  return provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined;
}
const TIMELINE_STATUS = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  }
} satisfies Record<TimelineStatus, {
  label: string;
  className: string;
  icon: React.ReactNode;
}>;
function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map(key => taskSteps.find(step => step.stepKey === key)).filter(Boolean);
  if (rows.some(row => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some(row => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }
  if (keys.every(key => taskSteps.some(step => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "automated";
  }
  return "waiting";
}
function TimelineStepCard(t0) {
  const $ = _c(23);
  const {
    task,
    step,
    failedStepKey
  } = t0;
  const effectiveStatus = failedStepKey ? "failed" : step.status;
  const status = TIMELINE_STATUS[effectiveStatus];
  const isSkipped = effectiveStatus === "not_needed";
  let t1;
  if ($[0] !== failedStepKey || $[1] !== step.continuation || $[2] !== task) {
    t1 = failedStepKey ? {
      instruction: "After completing this step manually, continue with the remaining steps.",
      control: <TprManualStepContinueButton task={task} stepKey={failedStepKey} />
    } : step.continuation;
    $[0] = failedStepKey;
    $[1] = step.continuation;
    $[2] = task;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const continuation = t1;
  const cardVariant = effectiveStatus === "automated" || effectiveStatus === "verified" ? "success" : effectiveStatus === "failed" ? "destructive" : effectiveStatus === "needs_action" || effectiveStatus === "verify" ? "warn" : "outline";
  let t2;
  if ($[4] !== continuation) {
    t2 = continuation ? <div className="space-y-3 border-t pt-4"><p className="text-sm text-muted-foreground">{continuation.instruction}</p><div className="flex justify-center">{continuation.control}</div></div> : null;
    $[4] = continuation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== step.content || $[7] !== t2) {
    t3 = <>{step.content}{t2}</>;
    $[6] = step.content;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const body = t3;
  const t4 = `${step.number}. ${step.title}`;
  const t5 = effectiveStatus === "automated" ? undefined : status.icon;
  const t6 = effectiveStatus === "not_run" || isSkipped ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined;
  let t7;
  if ($[9] !== effectiveStatus || $[10] !== status) {
    t7 = effectiveStatus === "automated" ? <span className="text-success-foreground" aria-label="Automated"><CheckCircleIcon weight="fill" className="size-5" /></span> : <Badge variant="outline" className={status.className}>{status.label}</Badge>;
    $[9] = effectiveStatus;
    $[10] = status;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== body || $[13] !== isSkipped || $[14] !== step.summary) {
    t8 = isSkipped ? <details><summary className="cursor-pointer text-sm text-muted-foreground">{step.summary}</summary><div className="mt-3 space-y-3">{body}</div></details> : <><p className="text-sm text-muted-foreground">{step.summary}</p>{body}</>;
    $[12] = body;
    $[13] = isSkipped;
    $[14] = step.summary;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== cardVariant || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = <ValerieCard title={t4} icon={t5} className={t6} variant={cardVariant} actions={t7} contentClassName="space-y-3">{t8}</ValerieCard>;
    $[16] = cardVariant;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
function TprStediEligibilityValues(t0) {
  const $ = _c(5);
  const {
    referralId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referralId);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm text-muted-foreground">Recheck or edit coverage on the Information tab.</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function TprSyncTimeline(t0) {
  const $ = _c(423);
  const {
    task,
    referralId,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    resolvedReceiving,
    patientLabel,
    patientEhrId,
    documentId,
    bucketLabel,
    internalNoteValue,
    actionNote,
    decisionTrace,
    insuranceStatusDisplay,
    hasMissingInformation,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    upcomingAppointments,
    pastAppointments,
    documentUploadLabels,
    privacyNoticeOnFile,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    patientDisplayId,
    opsCrossCheck
  } = t0;
  let t1;
  if ($[0] !== taskSteps) {
    t1 = taskSteps.some(_temp3);
    $[0] = taskSteps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isProviderFirst = t1;
  let t2;
  if ($[2] !== pastAppointments || $[3] !== resolvedReceiving.providerSelectionEvidence || $[4] !== upcomingAppointments) {
    t2 = resolvedReceiving.providerSelectionEvidence ?? {
      selectedStep: null,
      upcomingAppointmentCount: upcomingAppointments.length,
      pastAppointmentCount: pastAppointments.length,
      appointmentProviders: []
    };
    $[2] = pastAppointments;
    $[3] = resolvedReceiving.providerSelectionEvidence;
    $[4] = upcomingAppointments;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const providerSelectionEvidence = t2;
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  let coverageApproval;
  let failedStepCard;
  let opsApproval;
  let steps;
  let t3;
  let waitingForAssociatedReferral;
  if ($[6] !== actionNote || $[7] !== bucketLabel || $[8] !== coverageReadback || $[9] !== decisionTrace || $[10] !== documentId || $[11] !== documentUploadLabels || $[12] !== followThroughEligible || $[13] !== hasMissingInformation || $[14] !== insuranceRequirements || $[15] !== insuranceStatusDisplay || $[16] !== internalNoteValue || $[17] !== isPrimaryCareProvider || $[18] !== isProviderFirst || $[19] !== opsCrossCheck.diagnosis || $[20] !== opsCrossCheck.emergencyContactName || $[21] !== opsCrossCheck.emergencyContactPhoneNumber || $[22] !== opsCrossCheck.emergencyContactRelationship || $[23] !== opsCrossCheck.isSpecialCaseInsurance || $[24] !== opsCrossCheck.patientDateOfBirth || $[25] !== opsCrossCheck.patientEthnicity || $[26] !== opsCrossCheck.patientFaxNumber || $[27] !== opsCrossCheck.patientHomeAddress || $[28] !== opsCrossCheck.patientLanguage || $[29] !== opsCrossCheck.patientName || $[30] !== opsCrossCheck.patientPhoneNumber || $[31] !== opsCrossCheck.patientRace || $[32] !== opsCrossCheck.primaryPlanLabel || $[33] !== opsCrossCheck.primarySubscriberId || $[34] !== opsCrossCheck.receivingProviderName || $[35] !== opsCrossCheck.receivingProviderNpi || $[36] !== opsCrossCheck.referralDate || $[37] !== opsCrossCheck.referringLocationAddress || $[38] !== opsCrossCheck.referringProviderName || $[39] !== opsCrossCheck.referringProviderNpi || $[40] !== opsCrossCheck.referringProviderSpecialty || $[41] !== patientDisplayId || $[42] !== patientEhrId || $[43] !== patientLabel || $[44] !== pendingApproval || $[45] !== primaryCareProviderAlreadyPresent || $[46] !== privacyNoticeOnFile || $[47] !== providerSelectionEvidence || $[48] !== receivingAssignmentConfirmed || $[49] !== referralAuthorization || $[50] !== referralId || $[51] !== resolvedReceiving.advisories || $[52] !== resolvedReceiving.flags || $[53] !== resolvedReceiving.locationLabel || $[54] !== resolvedReceiving.providerLabel || $[55] !== resolvedReceiving.providerNpi || $[56] !== resolvedReceiving.providerWriteSafe || $[57] !== resolvedReceiving.routedToSscardio || $[58] !== resolvedReceiving.usualProviderWriteSafe || $[59] !== task || $[60] !== taskSteps || $[61] !== unresolvedUpcomingProviders || $[62] !== upcomingAppointmentProviderSummary || $[63] !== workflowStillProcessing) {
    const assignmentStatus = isProviderFirst ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT]) : workflowStillProcessing ? "waiting" : "not_run";
    const assignmentDependentStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
    const receivingAssignmentApproval = pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
    const assignmentRequiresManualReview = assignmentStatus === "automated" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
    const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === "usual_provider";
    const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
    const assignmentWasManuallyConfirmed = assignmentStatus === "automated" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
    const assignmentReviewStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentRequiresManualReview || usesRetiredUsualProviderRule ? "needs_action" : assignmentDependentStatus;
    const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
    const assignmentConfirmationStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentReviewStatus === "needs_action" ? "needs_action" : assignmentStatus === "automated" ? gateStatus : assignmentStatus;
    const confirmedNonCamelbackAssignment = assignmentStatus === "automated" && !followThroughEligible && receivingAssignmentApproval === null;
    const skippedFollowThroughStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
    const skippedFollowThroughSummary = assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : confirmedNonCamelbackAssignment ? "Not needed for the resolved non-Camelback receiving assignment." : "Not run because the receiving assignment has not completed.";
    coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
    opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
    const opsKind = opsApproval?.metadata?.kind ?? null;
    let t4;
    if ($[70] !== taskSteps) {
      t4 = taskSteps.some(_temp4);
      $[70] = taskSteps;
      $[71] = t4;
    } else {
      t4 = $[71];
    }
    waitingForAssociatedReferral = t4;
    const coverageReadbackComplete = taskSteps.some(_temp5);
    let t5;
    if ($[72] !== taskSteps) {
      t5 = taskSteps.some(_temp6);
      $[72] = taskSteps;
      $[73] = t5;
    } else {
      t5 = $[73];
    }
    const authRequestUploaded = t5;
    let t6;
    if ($[74] !== taskSteps) {
      t6 = taskSteps.some(_temp7);
      $[74] = taskSteps;
      $[75] = t6;
    } else {
      t6 = $[75];
    }
    const notesComplete = t6;
    let t7;
    if ($[76] !== taskSteps) {
      t7 = taskSteps.some(_temp8);
      $[76] = taskSteps;
      $[77] = t7;
    } else {
      t7 = $[77];
    }
    const missingInfoUploadComplete = t7;
    const verificationActionStepNumber = task.status === TaskStatus.VERIFICATION_NEEDED ? 11 : null;
    let t8;
    if ($[78] !== followThroughEligible || $[79] !== skippedFollowThroughStatus || $[80] !== taskSteps) {
      t8 = keys => followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
      $[78] = followThroughEligible;
      $[79] = skippedFollowThroughStatus;
      $[80] = taskSteps;
      $[81] = t8;
    } else {
      t8 = $[81];
    }
    const followThroughStatus = t8;
    const resolveConsolidatedStatus = _temp9;
    let coverageStatus = assignmentRequiresManualReview ? "not_run" : taskStepStatus(taskSteps, [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE]);
    if (coverageApproval) {
      coverageStatus = coverageApproval.metadata?.reviewMode === "verify" ? "verify" : "needs_action";
    } else {
      if (coverageReadbackComplete) {
        coverageStatus = "verified";
      }
    }
    const persistedAthenaEligibilityStatus = followThroughStatus([StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY]);
    const athenaEligibilityStatus = coverageReadback?.primary.selfPay ? "not_needed" : persistedAthenaEligibilityStatus === "automated" && insuranceStatusDisplay === "Unverified" ? "needs_action" : persistedAthenaEligibilityStatus;
    const networkStatus = coverageReadback?.primary.selfPay || referralAuthorization?.outcome === "not_needed" && referralAuthorization.reason === "ineligible" ? "not_needed" : insuranceRequirements && (insuranceRequirements.clinicNetwork === "manual_review" || insuranceRequirements.providerNetwork === "manual_review" || insuranceRequirements.additionalSchedulingInformationRequired) ? "needs_action" : followThroughStatus([StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS]);
    const authorizationStatus = opsKind === "authorization_review" ? "verify" : opsKind === "manual_authorization" ? "needs_action" : tprReferralAuthNeedsReview(referralAuthorization) ? "needs_action" : referralAuthorization?.outcome === "not_needed" ? "not_needed" : referralAuthorization?.outcome === "needs_pending_request" ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
    let t9;
    if ($[82] !== referralAuthorization) {
      t9 = tprReferralAuthNeedsPendingUpload(referralAuthorization);
      $[82] = referralAuthorization;
      $[83] = t9;
    } else {
      t9 = $[83];
    }
    const referralAuthNeedsPendingUpload = t9;
    const pcpFaxStatus = opsKind === "pcp_referral_fax" ? "needs_action" : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp ? "not_needed" : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
    const authRequestUploadStatus = opsKind === "auth_request_upload" ? "needs_action" : referralAuthNeedsPendingUpload ? authRequestUploaded ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD]) : "not_needed";
    const missingInfoFaxStatus = !hasMissingInformation ? "not_needed" : missingInfoUploadComplete ? "automated" : followThroughStatus([StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]);
    const outboundFaxStatus = resolveConsolidatedStatus([authRequestUploadStatus, missingInfoFaxStatus]);
    const closeDocumentStatus = opsKind === "close_document" ? "needs_action" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
    let fullRegistrationReviewReasons;
    let t10;
    if ($[84] !== privacyNoticeOnFile || $[85] !== task.files) {
      const workflowNote = task.files.find(_temp10)?.note;
      const t11 = privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically") ? "Set Athena Privacy to YES manually." : null;
      let t12;
      if ($[88] !== t11) {
        t12 = [t11].filter(_temp11);
        $[88] = t11;
        $[89] = t12;
      } else {
        t12 = $[89];
      }
      fullRegistrationReviewReasons = t12;
      t10 = workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team") ? "Add the unmatched referring provider to the Athena care team manually." : null;
      $[84] = privacyNoticeOnFile;
      $[85] = task.files;
      $[86] = fullRegistrationReviewReasons;
      $[87] = t10;
    } else {
      fullRegistrationReviewReasons = $[86];
      t10 = $[87];
    }
    let t11;
    if ($[90] !== t10) {
      t11 = [t10].filter(_temp12);
      $[90] = t10;
      $[91] = t11;
    } else {
      t11 = $[91];
    }
    const demographicsReviewReasons = t11;
    const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
    const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
    const fullRegistrationStatus = ensurePatientStatus === "failed" ? "not_run" : ensurePatientStatus === "waiting" ? "waiting" : fullRegistrationReviewReasons.length > 0 ? "needs_action" : "automated";
    const demographicsStatus = ensurePatientStatus === "failed" ? "not_run" : careTeamStatus !== "automated" ? careTeamStatus : demographicsReviewReasons.length > 0 ? "needs_action" : "automated";
    const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
    const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
    const inNetworkDisplay = !insuranceRequirements ? unevaluatedInsuranceDecision : insuranceRequirements.clinicNetwork === "out_of_network" || insuranceRequirements.providerNetwork === "out_of_network" ? "No" : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network" ? "Yes" : insuranceRequirements.clinicNetwork === "not_applicable" && insuranceRequirements.providerNetwork === "not_applicable" ? "Not applicable" : "Manual review";
    let t12;
    if ($[92] !== insuranceRequirements || $[93] !== unevaluatedInsuranceDecision) {
      t12 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement) : unevaluatedInsuranceDecision;
      $[92] = insuranceRequirements;
      $[93] = unevaluatedInsuranceDecision;
      $[94] = t12;
    } else {
      t12 = $[94];
    }
    const referralRequirementDisplay = t12;
    let t13;
    if ($[95] !== insuranceRequirements || $[96] !== unevaluatedInsuranceDecision) {
      t13 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement) : unevaluatedInsuranceDecision;
      $[95] = insuranceRequirements;
      $[96] = unevaluatedInsuranceDecision;
      $[97] = t13;
    } else {
      t13 = $[97];
    }
    const authorizationRequirementDisplay = t13;
    let t14;
    if ($[98] !== opsCrossCheck.primaryPlanLabel) {
      t14 = {
        label: "Valerie plan",
        value: opsCrossCheck.primaryPlanLabel
      };
      $[98] = opsCrossCheck.primaryPlanLabel;
      $[99] = t14;
    } else {
      t14 = $[99];
    }
    let t15;
    if ($[100] !== opsCrossCheck.primarySubscriberId) {
      t15 = {
        label: "Valerie subscriber ID",
        value: opsCrossCheck.primarySubscriberId
      };
      $[100] = opsCrossCheck.primarySubscriberId;
      $[101] = t15;
    } else {
      t15 = $[101];
    }
    let t16;
    if ($[102] !== inNetworkDisplay) {
      t16 = {
        label: "In network",
        value: inNetworkDisplay
      };
      $[102] = inNetworkDisplay;
      $[103] = t16;
    } else {
      t16 = $[103];
    }
    let t17;
    if ($[104] !== referralRequirementDisplay) {
      t17 = {
        label: "Referral",
        value: referralRequirementDisplay
      };
      $[104] = referralRequirementDisplay;
      $[105] = t17;
    } else {
      t17 = $[105];
    }
    let t18;
    if ($[106] !== authorizationRequirementDisplay) {
      t18 = {
        label: "Authorization",
        value: authorizationRequirementDisplay
      };
      $[106] = authorizationRequirementDisplay;
      $[107] = t18;
    } else {
      t18 = $[107];
    }
    let t19;
    if ($[108] !== t14 || $[109] !== t15 || $[110] !== t16 || $[111] !== t17 || $[112] !== t18) {
      t19 = [t14, t15, t16, t17, t18];
      $[108] = t14;
      $[109] = t15;
      $[110] = t16;
      $[111] = t17;
      $[112] = t18;
      $[113] = t19;
    } else {
      t19 = $[113];
    }
    const insuranceCrossCheckItems = t19;
    const insuranceIntakeDetails = coverageApproval?.metadata ? <div className="space-y-3 rounded-md border p-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "Expected",
        value: coverageApproval.metadata.expectedCoverageCount
      }, {
        label: "In Athena",
        value: coverageApproval.metadata.currentCoverages.length
      }]} /><AthenaCoverageEvidence metadata={coverageApproval.metadata} /></div> : coverageReadback ? <div className="space-y-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "In Athena",
        value: coverageReadback.coverages.length
      }, {
        label: "Self pay",
        value: coverageReadback.primary.selfPay ? "Yes" : "No"
      }]} /><div className="space-y-1"><p className="font-medium">Currently in Athena</p>{coverageReadback.coverages.length === 0 ? <p>No coverage found.</p> : coverageReadback.coverages.map(_temp13)}</div></div> : followThroughEligible || assignmentStatus === "automated" ? <DecisionMetadata items={insuranceCrossCheckItems} /> : null;
    const t20 = `${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`;
    let t21;
    if ($[114] !== t20) {
      t21 = {
        label: "Patient",
        value: <CopyableContent text={t20} textClassName="text-xs" />
      };
      $[114] = t20;
      $[115] = t21;
    } else {
      t21 = $[115];
    }
    let t22;
    if ($[116] !== patientEhrId) {
      t22 = {
        label: "EHR ID",
        value: <CopyableContent text={patientEhrId} textClassName="text-xs" />
      };
      $[116] = patientEhrId;
      $[117] = t22;
    } else {
      t22 = $[117];
    }
    let t23;
    if ($[118] !== documentId) {
      t23 = {
        label: "Document ID",
        value: <CopyableContent text={documentId} textClassName="text-xs" />
      };
      $[118] = documentId;
      $[119] = t23;
    } else {
      t23 = $[119];
    }
    let t24;
    if ($[120] !== opsCrossCheck.patientPhoneNumber) {
      t24 = {
        label: "Phone",
        value: <CopyableContent text={opsCrossCheck.patientPhoneNumber} textClassName="text-xs" />
      };
      $[120] = opsCrossCheck.patientPhoneNumber;
      $[121] = t24;
    } else {
      t24 = $[121];
    }
    let t25;
    if ($[122] !== opsCrossCheck.patientFaxNumber) {
      t25 = {
        label: "Fax",
        value: <CopyableContent text={opsCrossCheck.patientFaxNumber} textClassName="text-xs" />
      };
      $[122] = opsCrossCheck.patientFaxNumber;
      $[123] = t25;
    } else {
      t25 = $[123];
    }
    let t26;
    if ($[124] !== opsCrossCheck.patientEthnicity) {
      t26 = {
        label: "Ethnicity",
        value: opsCrossCheck.patientEthnicity
      };
      $[124] = opsCrossCheck.patientEthnicity;
      $[125] = t26;
    } else {
      t26 = $[125];
    }
    let t27;
    if ($[126] !== opsCrossCheck.patientRace) {
      t27 = {
        label: "Race",
        value: opsCrossCheck.patientRace
      };
      $[126] = opsCrossCheck.patientRace;
      $[127] = t27;
    } else {
      t27 = $[127];
    }
    let t28;
    if ($[128] !== opsCrossCheck.patientLanguage) {
      t28 = {
        label: "Language",
        value: opsCrossCheck.patientLanguage
      };
      $[128] = opsCrossCheck.patientLanguage;
      $[129] = t28;
    } else {
      t28 = $[129];
    }
    let t29;
    if ($[130] !== t21 || $[131] !== t22 || $[132] !== t23 || $[133] !== t24 || $[134] !== t25 || $[135] !== t26 || $[136] !== t27 || $[137] !== t28) {
      t29 = <DecisionMetadata items={[t21, t22, t23, t24, t25, t26, t27, t28]} />;
      $[130] = t21;
      $[131] = t22;
      $[132] = t23;
      $[133] = t24;
      $[134] = t25;
      $[135] = t26;
      $[136] = t27;
      $[137] = t28;
      $[138] = t29;
    } else {
      t29 = $[138];
    }
    let t30;
    if ($[139] !== patientDisplayId) {
      t30 = patientDisplayId ? <Link href={`/patients/${patientDisplayId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">Open patient details<SquareArrowOutUpRight className="size-3.5" /></Link> : null;
      $[139] = patientDisplayId;
      $[140] = t30;
    } else {
      t30 = $[140];
    }
    let t31;
    if ($[141] !== t29 || $[142] !== t30) {
      t31 = <div className="space-y-3">{t29}{t30}</div>;
      $[141] = t29;
      $[142] = t30;
      $[143] = t31;
    } else {
      t31 = $[143];
    }
    let t32;
    if ($[144] !== patientLabel) {
      t32 = {
        label: "Patient type",
        value: patientLabel
      };
      $[144] = patientLabel;
      $[145] = t32;
    } else {
      t32 = $[145];
    }
    const t33 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    let t34;
    if ($[146] !== t33) {
      t34 = {
        label: "Provider",
        value: <CopyableContent text={t33} textClassName="text-xs" />
      };
      $[146] = t33;
      $[147] = t34;
    } else {
      t34 = $[147];
    }
    const t35 = resolvedReceiving.providerNpi || NOT_AVAILABLE;
    let t36;
    if ($[148] !== t35) {
      t36 = {
        label: "NPI",
        value: <CopyableContent text={t35} textClassName="text-xs" />
      };
      $[148] = t35;
      $[149] = t36;
    } else {
      t36 = $[149];
    }
    const t37 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t38;
    if ($[150] !== t37) {
      t38 = {
        label: "Location",
        value: <CopyableContent text={t37} textClassName="text-xs" />
      };
      $[150] = t37;
      $[151] = t38;
    } else {
      t38 = $[151];
    }
    const t39 = usesRetiredUsualProviderRule ? "Verify appointment history" : assignmentRequiresManualReview ? "Manual review" : "Confirmed";
    let t40;
    if ($[152] !== t39) {
      t40 = {
        label: "Resolution",
        value: t39
      };
      $[152] = t39;
      $[153] = t40;
    } else {
      t40 = $[153];
    }
    let t41;
    if ($[154] !== t32 || $[155] !== t34 || $[156] !== t36 || $[157] !== t38 || $[158] !== t40) {
      t41 = <DecisionMetadata items={[t32, t34, t36, t38, t40]} />;
      $[154] = t32;
      $[155] = t34;
      $[156] = t36;
      $[157] = t38;
      $[158] = t40;
      $[159] = t41;
    } else {
      t41 = $[159];
    }
    const t42 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    const t43 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t44;
    if ($[160] !== opsCrossCheck.diagnosis || $[161] !== opsCrossCheck.receivingProviderName || $[162] !== opsCrossCheck.receivingProviderNpi || $[163] !== providerSelectionEvidence || $[164] !== t42 || $[165] !== t43 || $[166] !== upcomingAppointmentProviderSummary) {
      t44 = <ProviderSelectionChecklist evidence={providerSelectionEvidence} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} referralProviderName={opsCrossCheck.receivingProviderName} referralProviderNpi={opsCrossCheck.receivingProviderNpi} diagnosis={opsCrossCheck.diagnosis} selectedProviderName={t42} selectedLocation={t43} />;
      $[160] = opsCrossCheck.diagnosis;
      $[161] = opsCrossCheck.receivingProviderName;
      $[162] = opsCrossCheck.receivingProviderNpi;
      $[163] = providerSelectionEvidence;
      $[164] = t42;
      $[165] = t43;
      $[166] = upcomingAppointmentProviderSummary;
      $[167] = t44;
    } else {
      t44 = $[167];
    }
    let t45;
    if ($[168] !== resolvedReceiving.advisories) {
      t45 = resolvedReceiving.advisories.length > 0 ? <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.advisories.map(_temp14)}</ul></div> : null;
      $[168] = resolvedReceiving.advisories;
      $[169] = t45;
    } else {
      t45 = $[169];
    }
    const t46 = assignmentConfirmationStatus === "needs_action" && resolvedReceiving.flags.length > 0 ? <><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.flags.map(_temp15)}</ul>{unresolvedUpcomingProviders.length > 0 ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3"><span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>{unresolvedUpcomingProviders.map(_temp16)}</div> : null}</> : null;
    let t47;
    if ($[170] !== t41 || $[171] !== t44 || $[172] !== t45 || $[173] !== t46) {
      t47 = <div className="space-y-3">{t41}{t44}{t45}{t46}</div>;
      $[170] = t41;
      $[171] = t44;
      $[172] = t45;
      $[173] = t46;
      $[174] = t47;
    } else {
      t47 = $[174];
    }
    let t48;
    if ($[175] === Symbol.for("react.memo_cache_sentinel")) {
      t48 = <p className="text-sm font-medium">Patient Details</p>;
      $[175] = t48;
    } else {
      t48 = $[175];
    }
    let t49;
    if ($[176] !== opsCrossCheck.patientName) {
      t49 = {
        label: "Patient",
        value: opsCrossCheck.patientName
      };
      $[176] = opsCrossCheck.patientName;
      $[177] = t49;
    } else {
      t49 = $[177];
    }
    let t50;
    if ($[178] !== opsCrossCheck.patientDateOfBirth) {
      t50 = {
        label: "DOB",
        value: opsCrossCheck.patientDateOfBirth
      };
      $[178] = opsCrossCheck.patientDateOfBirth;
      $[179] = t50;
    } else {
      t50 = $[179];
    }
    let t51;
    if ($[180] !== opsCrossCheck.patientPhoneNumber) {
      t51 = {
        label: "Phone",
        value: opsCrossCheck.patientPhoneNumber
      };
      $[180] = opsCrossCheck.patientPhoneNumber;
      $[181] = t51;
    } else {
      t51 = $[181];
    }
    let t52;
    if ($[182] !== opsCrossCheck.patientHomeAddress) {
      t52 = {
        label: "Home address",
        value: opsCrossCheck.patientHomeAddress
      };
      $[182] = opsCrossCheck.patientHomeAddress;
      $[183] = t52;
    } else {
      t52 = $[183];
    }
    let t53;
    if ($[184] !== t49 || $[185] !== t50 || $[186] !== t51 || $[187] !== t52) {
      t53 = <div className="space-y-2 rounded-md border p-3">{t48}<DecisionMetadata items={[t49, t50, t51, t52]} /></div>;
      $[184] = t49;
      $[185] = t50;
      $[186] = t51;
      $[187] = t52;
      $[188] = t53;
    } else {
      t53 = $[188];
    }
    let t54;
    if ($[189] === Symbol.for("react.memo_cache_sentinel")) {
      t54 = <p className="text-sm font-medium">Care Team</p>;
      $[189] = t54;
    } else {
      t54 = $[189];
    }
    let t55;
    if ($[190] === Symbol.for("react.memo_cache_sentinel")) {
      t55 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p>;
      $[190] = t55;
    } else {
      t55 = $[190];
    }
    let t56;
    if ($[191] !== resolvedReceiving.providerLabel || $[192] !== resolvedReceiving.providerNpi || $[193] !== resolvedReceiving.usualProviderWriteSafe) {
      t56 = <div className="space-y-1">{t55}<DecisionMetadata items={resolvedReceiving.usualProviderWriteSafe ? [{
          label: "Provider",
          value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Action",
          value: "Update (new patient)"
        }] : [{
          label: "Action",
          value: "Leave unchanged (established patient)"
        }]} /></div>;
      $[191] = resolvedReceiving.providerLabel;
      $[192] = resolvedReceiving.providerNpi;
      $[193] = resolvedReceiving.usualProviderWriteSafe;
      $[194] = t56;
    } else {
      t56 = $[194];
    }
    let t57;
    if ($[195] === Symbol.for("react.memo_cache_sentinel")) {
      t57 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p>;
      $[195] = t57;
    } else {
      t57 = $[195];
    }
    let t58;
    if ($[196] !== opsCrossCheck.referringProviderName) {
      t58 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
      };
      $[196] = opsCrossCheck.referringProviderName;
      $[197] = t58;
    } else {
      t58 = $[197];
    }
    let t59;
    if ($[198] !== opsCrossCheck.referringProviderNpi) {
      t59 = {
        label: "NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
      };
      $[198] = opsCrossCheck.referringProviderNpi;
      $[199] = t59;
    } else {
      t59 = $[199];
    }
    let t60;
    if ($[200] !== opsCrossCheck.referringLocationAddress) {
      t60 = {
        label: "Address",
        value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
      };
      $[200] = opsCrossCheck.referringLocationAddress;
      $[201] = t60;
    } else {
      t60 = $[201];
    }
    let t61;
    if ($[202] !== opsCrossCheck.referringProviderSpecialty) {
      t61 = {
        label: "Specialty",
        value: opsCrossCheck.referringProviderSpecialty
      };
      $[202] = opsCrossCheck.referringProviderSpecialty;
      $[203] = t61;
    } else {
      t61 = $[203];
    }
    let t62;
    if ($[204] !== t58 || $[205] !== t59 || $[206] !== t60 || $[207] !== t61) {
      t62 = <div className="space-y-1">{t57}<DecisionMetadata items={[t58, t59, t60, t61]} /></div>;
      $[204] = t58;
      $[205] = t59;
      $[206] = t60;
      $[207] = t61;
      $[208] = t62;
    } else {
      t62 = $[208];
    }
    let t63;
    if ($[209] !== isPrimaryCareProvider || $[210] !== opsCrossCheck.referringProviderName || $[211] !== opsCrossCheck.referringProviderNpi || $[212] !== primaryCareProviderAlreadyPresent) {
      t63 = isPrimaryCareProvider && !primaryCareProviderAlreadyPresent ? <div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">PCP</p><DecisionMetadata items={[{
          label: "Name",
          value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
        }]} /></div> : null;
      $[209] = isPrimaryCareProvider;
      $[210] = opsCrossCheck.referringProviderName;
      $[211] = opsCrossCheck.referringProviderNpi;
      $[212] = primaryCareProviderAlreadyPresent;
      $[213] = t63;
    } else {
      t63 = $[213];
    }
    let t64;
    if ($[214] !== t56 || $[215] !== t62 || $[216] !== t63) {
      t64 = <div className="space-y-3 rounded-md border p-3">{t54}{t56}{t62}{t63}</div>;
      $[214] = t56;
      $[215] = t62;
      $[216] = t63;
      $[217] = t64;
    } else {
      t64 = $[217];
    }
    let t65;
    if ($[218] === Symbol.for("react.memo_cache_sentinel")) {
      t65 = <p className="text-sm font-medium">Emergency Contact</p>;
      $[218] = t65;
    } else {
      t65 = $[218];
    }
    let t66;
    if ($[219] !== opsCrossCheck.emergencyContactName) {
      t66 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.emergencyContactName} textClassName="text-xs" />
      };
      $[219] = opsCrossCheck.emergencyContactName;
      $[220] = t66;
    } else {
      t66 = $[220];
    }
    let t67;
    if ($[221] !== opsCrossCheck.emergencyContactRelationship) {
      t67 = {
        label: "Relationship",
        value: <CopyableContent text={opsCrossCheck.emergencyContactRelationship} textClassName="text-xs" />
      };
      $[221] = opsCrossCheck.emergencyContactRelationship;
      $[222] = t67;
    } else {
      t67 = $[222];
    }
    let t68;
    if ($[223] !== opsCrossCheck.emergencyContactPhoneNumber) {
      t68 = {
        label: "Phone",
        value: <CopyableContent text={opsCrossCheck.emergencyContactPhoneNumber} textClassName="text-xs" />
      };
      $[223] = opsCrossCheck.emergencyContactPhoneNumber;
      $[224] = t68;
    } else {
      t68 = $[224];
    }
    let t69;
    if ($[225] !== t66 || $[226] !== t67 || $[227] !== t68) {
      t69 = <div className="space-y-2 rounded-md border p-3">{t65}<DecisionMetadata items={[t66, t67, t68]} /></div>;
      $[225] = t66;
      $[226] = t67;
      $[227] = t68;
      $[228] = t69;
    } else {
      t69 = $[228];
    }
    let t70;
    if ($[229] !== demographicsReviewReasons) {
      t70 = demographicsReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{demographicsReviewReasons.map(_temp17)}</ul> : null;
      $[229] = demographicsReviewReasons;
      $[230] = t70;
    } else {
      t70 = $[230];
    }
    let t71;
    if ($[231] !== t53 || $[232] !== t64 || $[233] !== t69 || $[234] !== t70) {
      t71 = <div className="space-y-3">{t53}{t64}{t69}{t70}</div>;
      $[231] = t53;
      $[232] = t64;
      $[233] = t69;
      $[234] = t70;
      $[235] = t71;
    } else {
      t71 = $[235];
    }
    let t72;
    if ($[236] !== opsCrossCheck.primaryPlanLabel) {
      t72 = <CopyableContent text={opsCrossCheck.primaryPlanLabel} textClassName="text-xs" />;
      $[236] = opsCrossCheck.primaryPlanLabel;
      $[237] = t72;
    } else {
      t72 = $[237];
    }
    let t73;
    if ($[238] !== opsCrossCheck.isSpecialCaseInsurance) {
      t73 = opsCrossCheck.isSpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn-foreground">Special Case Insurance</Badge> : null;
      $[238] = opsCrossCheck.isSpecialCaseInsurance;
      $[239] = t73;
    } else {
      t73 = $[239];
    }
    let t74;
    if ($[240] !== t72 || $[241] !== t73) {
      t74 = {
        label: "Plan (from Valerie)",
        value: <span className="inline-flex items-center gap-1.5">{t72}{t73}</span>
      };
      $[240] = t72;
      $[241] = t73;
      $[242] = t74;
    } else {
      t74 = $[242];
    }
    let t75;
    if ($[243] !== opsCrossCheck.primarySubscriberId) {
      t75 = {
        label: "Subscriber ID (from Valerie)",
        value: <CopyableContent text={opsCrossCheck.primarySubscriberId} textClassName="text-xs" />
      };
      $[243] = opsCrossCheck.primarySubscriberId;
      $[244] = t75;
    } else {
      t75 = $[244];
    }
    const t76 = coverageReadbackComplete ? "Yes" : "No";
    let t77;
    if ($[245] !== t76) {
      t77 = {
        label: "Synced to Athena",
        value: t76
      };
      $[245] = t76;
      $[246] = t77;
    } else {
      t77 = $[246];
    }
    const t78 = coverageReadback?.primary.selfPay ? "N/A \u2014 Self Pay" : inNetworkDisplay;
    let t79;
    if ($[247] !== t78) {
      t79 = {
        label: "In network",
        value: t78
      };
      $[247] = t78;
      $[248] = t79;
    } else {
      t79 = $[248];
    }
    let t80;
    if ($[249] !== t74 || $[250] !== t75 || $[251] !== t77 || $[252] !== t79) {
      t80 = <DecisionMetadata items={[t74, t75, t77, t79]} />;
      $[249] = t74;
      $[250] = t75;
      $[251] = t77;
      $[252] = t79;
      $[253] = t80;
    } else {
      t80 = $[253];
    }
    let t81;
    if ($[254] === Symbol.for("react.memo_cache_sentinel")) {
      t81 = <summary className="cursor-pointer text-muted-foreground">More insurance detail</summary>;
      $[254] = t81;
    } else {
      t81 = $[254];
    }
    let t82;
    if ($[255] === Symbol.for("react.memo_cache_sentinel")) {
      t82 = <p className="text-sm font-medium">Coverage</p>;
      $[255] = t82;
    } else {
      t82 = $[255];
    }
    const t83 = coverageReadback?.primary.selfPay ? "Yes" : "No";
    let t84;
    if ($[256] !== t83) {
      t84 = <DecisionMetadata items={[{
        label: "Self pay",
        value: t83
      }]} />;
      $[256] = t83;
      $[257] = t84;
    } else {
      t84 = $[257];
    }
    let t85;
    if ($[258] !== referralId) {
      t85 = <TprStediEligibilityValues referralId={referralId} />;
      $[258] = referralId;
      $[259] = t85;
    } else {
      t85 = $[259];
    }
    let t86;
    if ($[260] !== insuranceIntakeDetails) {
      t86 = insuranceIntakeDetails ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Show Athena coverage detail</summary><div className="mt-2">{insuranceIntakeDetails}</div></details> : null;
      $[260] = insuranceIntakeDetails;
      $[261] = t86;
    } else {
      t86 = $[261];
    }
    let t87;
    if ($[262] !== t84 || $[263] !== t85 || $[264] !== t86) {
      t87 = <div className="space-y-2 rounded-md border p-3">{t82}{t84}{t85}{t86}</div>;
      $[262] = t84;
      $[263] = t85;
      $[264] = t86;
      $[265] = t87;
    } else {
      t87 = $[265];
    }
    let t88;
    if ($[266] === Symbol.for("react.memo_cache_sentinel")) {
      t88 = <p className="text-sm font-medium">Athena Eligibility</p>;
      $[266] = t88;
    } else {
      t88 = $[266];
    }
    let t89;
    if ($[267] !== followThroughEligible || $[268] !== insuranceStatusDisplay || $[269] !== skippedFollowThroughSummary) {
      t89 = <div className="space-y-2 rounded-md border p-3">{t88}{followThroughEligible ? <DecisionMetadata items={[{
          label: "Athena eligibility",
          value: insuranceStatusDisplay
        }]} /> : <p className="text-sm text-muted-foreground">{skippedFollowThroughSummary}</p>}</div>;
      $[267] = followThroughEligible;
      $[268] = insuranceStatusDisplay;
      $[269] = skippedFollowThroughSummary;
      $[270] = t89;
    } else {
      t89 = $[270];
    }
    let t90;
    if ($[271] !== insuranceRequirements) {
      t90 = insuranceRequirements?.reviewReasons.length ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Network Review Reasons</p><ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{insuranceRequirements.reviewReasons.map(_temp18)}</ul></div> : null;
      $[271] = insuranceRequirements;
      $[272] = t90;
    } else {
      t90 = $[272];
    }
    let t91;
    if ($[273] !== t87 || $[274] !== t89 || $[275] !== t90) {
      t91 = <details className="text-xs">{t81}<div className="mt-3 space-y-3">{t87}{t89}{t90}</div></details>;
      $[273] = t87;
      $[274] = t89;
      $[275] = t90;
      $[276] = t91;
    } else {
      t91 = $[276];
    }
    let t92;
    if ($[277] !== t80 || $[278] !== t91) {
      t92 = <div className="space-y-3">{t80}{t91}</div>;
      $[277] = t80;
      $[278] = t91;
      $[279] = t92;
    } else {
      t92 = $[279];
    }
    let t93;
    if ($[280] !== followThroughEligible || $[281] !== opsApproval?.metadata?.instruction || $[282] !== opsKind || $[283] !== referralAuthorization || $[284] !== skippedFollowThroughSummary) {
      t93 = opsKind === "pcp_referral_fax" ? opsApproval?.metadata?.instruction ?? "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax." : opsKind === "authorization_review" ? opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue." : opsKind === "manual_authorization" ? opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION : referralAuthorization?.outcome === "created" ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.` : referralAuthorization?.outcome === "needs_pending_request" ? "No authorization number is on file. An outbound request letter is required next." : tprReferralAuthNeedsReview(referralAuthorization) ? "No Athena object was created because the authorization requirements need manual review." : referralAuthorization?.outcome === "not_needed" ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).` : followThroughEligible ? "Waiting for network, authorization, and referral-requirement decisions." : skippedFollowThroughSummary;
      $[280] = followThroughEligible;
      $[281] = opsApproval?.metadata?.instruction;
      $[282] = opsKind;
      $[283] = referralAuthorization;
      $[284] = skippedFollowThroughSummary;
      $[285] = t93;
    } else {
      t93 = $[285];
    }
    let t94;
    if ($[286] === Symbol.for("react.memo_cache_sentinel")) {
      t94 = <p className="text-sm font-medium">PCP Referral Fax</p>;
      $[286] = t94;
    } else {
      t94 = $[286];
    }
    let t95;
    if ($[287] !== followThroughEligible || $[288] !== insuranceRequirements || $[289] !== isPrimaryCareProvider || $[290] !== skippedFollowThroughSummary) {
      t95 = <div className="space-y-2 rounded-md border p-3">{t94}{insuranceRequirements ? <DecisionMetadata items={[{
          label: "PCP referral",
          value: insuranceRequirements.referralMustBeFromPcp ? "Required" : "No"
        }, {
          label: "Referring is PCP",
          value: isPrimaryCareProvider ? "Yes" : "No"
        }]} /> : <p className="text-sm text-muted-foreground">{followThroughEligible ? "Waiting for network/requirements decision." : skippedFollowThroughSummary}</p>}</div>;
      $[287] = followThroughEligible;
      $[288] = insuranceRequirements;
      $[289] = isPrimaryCareProvider;
      $[290] = skippedFollowThroughSummary;
      $[291] = t95;
    } else {
      t95 = $[291];
    }
    let t96;
    if ($[292] === Symbol.for("react.memo_cache_sentinel")) {
      t96 = <p className="text-sm font-medium">Referral Authorization</p>;
      $[292] = t96;
    } else {
      t96 = $[292];
    }
    let t97;
    if ($[293] !== insuranceRequirements || $[294] !== referralAuthorization) {
      t97 = insuranceRequirements ? <DecisionMetadata items={[{
        label: "Authorization",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement)
      }, {
        label: "Referral",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement)
      }, {
        label: "Outcome",
        value: referralAuthorization ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome) : "Pending"
      }, ...(referralAuthorization?.outcome === "created" ? [{
        label: "Athena object",
        value: <CopyableContent text={referralAuthorization.referralAuthId} textClassName="text-xs" />
      }, {
        label: "Appointment",
        value: referralAuthorization.needsManualAppointmentAttach ? "Attach manually" : referralAuthorization.appointmentAttached ? "Attached" : "None in window"
      }] : [])]} /> : null;
      $[293] = insuranceRequirements;
      $[294] = referralAuthorization;
      $[295] = t97;
    } else {
      t97 = $[295];
    }
    let t98;
    if ($[296] === Symbol.for("react.memo_cache_sentinel")) {
      t98 = <summary className="cursor-pointer text-muted-foreground">How to complete this manually</summary>;
      $[296] = t98;
    } else {
      t98 = $[296];
    }
    let t99;
    if ($[297] === Symbol.for("react.memo_cache_sentinel")) {
      t99 = <p className="font-medium text-foreground">Referral Required</p>;
      $[297] = t99;
    } else {
      t99 = $[297];
    }
    let t100;
    if ($[298] === Symbol.for("react.memo_cache_sentinel")) {
      t100 = <li>If a referral is required, confirm the received referral automatically created an object under the Authorization section.</li>;
      $[298] = t100;
    } else {
      t100 = $[298];
    }
    let t101;
    let t102;
    if ($[299] === Symbol.for("react.memo_cache_sentinel")) {
      t101 = <div>{t99}<ul className="list-disc space-y-1 pl-5 text-muted-foreground">{t100}<li>If it did not, manually create the referral object with:<ul className="list-disc space-y-1 pl-5"><li>Insurance / authorization number: always "paper"</li><li>Referral dates — default (if not provided): today through 1 year later</li><li>Number of visits — default (if not provided): 12 visits</li><li>ICD-10 codes</li><li>Referring provider</li><li>Specialty</li><li>Only if listed on the referral doc: receiving provider and department</li></ul></li></ul></div>;
      t102 = <p className="font-medium text-foreground">Referral Authorization Required</p>;
      $[299] = t101;
      $[300] = t102;
    } else {
      t101 = $[299];
      t102 = $[300];
    }
    let t103;
    if ($[301] === Symbol.for("react.memo_cache_sentinel")) {
      t103 = <li>Same workflow as above, but enter the authorization number in the Insurance / authorization number field.</li>;
      $[301] = t103;
    } else {
      t103 = $[301];
    }
    let t104;
    if ($[302] === Symbol.for("react.memo_cache_sentinel")) {
      t104 = <details className="text-xs">{t98}<div className="mt-2 space-y-3">{t101}<div>{t102}<ul className="list-disc space-y-1 pl-5 text-muted-foreground">{t103}<li>Only send a fax if there is no authorization number. When sending an authorization request for a missing auth number:<ul className="list-disc space-y-1 pl-5"><li>Manually export the doc to the referring provider via AthenaFax.</li><li>Add an authorization object with "Pending" in the auth number field.</li><li>In all notes (patient notes, document internal note, action note): "Referral Authorization Requested [Date]"</li></ul></li></ul></div></div></details>;
      $[302] = t104;
    } else {
      t104 = $[302];
    }
    let t105;
    if ($[303] !== t97) {
      t105 = <div className="space-y-2 rounded-md border p-3">{t96}{t97}{t104}</div>;
      $[303] = t97;
      $[304] = t105;
    } else {
      t105 = $[304];
    }
    let t106;
    if ($[305] !== t105 || $[306] !== t95) {
      t106 = <div className="space-y-3">{t95}{t105}</div>;
      $[305] = t105;
      $[306] = t95;
      $[307] = t106;
    } else {
      t106 = $[307];
    }
    let t107;
    if ($[308] !== authRequestUploaded || $[309] !== referralAuthNeedsPendingUpload) {
      t107 = referralAuthNeedsPendingUpload || authRequestUploaded ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Request Letter</p><DecisionMetadata items={[{
          label: "Request letter",
          value: authRequestUploaded ? "Uploaded" : "Waiting for ops"
        }]} /><ol className="list-decimal space-y-1 pl-5 text-sm"><li>Export the patient chart in Athena. Do not skip this export.</li><li>Click Upload request letter. The letter is not uploaded automatically.</li><li>Athena then receives a Referral Auth object with number pending.</li></ol></div> : null;
      $[308] = authRequestUploaded;
      $[309] = referralAuthNeedsPendingUpload;
      $[310] = t107;
    } else {
      t107 = $[310];
    }
    let t108;
    if ($[311] !== hasMissingInformation || $[312] !== missingInfoUploadComplete) {
      t108 = hasMissingInformation ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Missing Patient Phone Packet</p><DecisionMetadata items={[{
          label: "Missing-info packet",
          value: missingInfoUploadComplete ? "Sent to Athena chart" : "Waiting for automation"
        }]} /><p className="text-sm text-muted-foreground">Generated automatically when the patient phone number is missing — no upload needed here. Once it appears in the Athena chart, send it from Athena like any other outbound fax.</p></div> : null;
      $[311] = hasMissingInformation;
      $[312] = missingInfoUploadComplete;
      $[313] = t108;
    } else {
      t108 = $[313];
    }
    const t109 = outboundFaxStatus === "not_needed" ? <p className="text-sm text-muted-foreground">Nothing to send — the referral authorization doesn't need an outbound request, and the patient phone number isn't missing.</p> : null;
    let t110;
    if ($[314] !== t107 || $[315] !== t108 || $[316] !== t109) {
      t110 = <div className="space-y-3">{t107}{t108}{t109}</div>;
      $[314] = t107;
      $[315] = t108;
      $[316] = t109;
      $[317] = t110;
    } else {
      t110 = $[317];
    }
    let t111;
    let t112;
    if ($[318] === Symbol.for("react.memo_cache_sentinel")) {
      t111 = {
        label: "Calls",
        value: "Set to Yes only if blank \u2014 existing value is not overridden"
      };
      t112 = {
        label: "Texts",
        value: "Set to Yes only if blank \u2014 existing value is not overridden"
      };
      $[318] = t111;
      $[319] = t112;
    } else {
      t111 = $[318];
      t112 = $[319];
    }
    const t113 = privacyNoticeOnFile === null ? "Verify selected" : privacyNoticeOnFile ? "Selected" : "Not selected";
    let t114;
    if ($[320] !== t113) {
      t114 = {
        label: "Privacy Notice",
        value: t113
      };
      $[320] = t113;
      $[321] = t114;
    } else {
      t114 = $[321];
    }
    let t115;
    if ($[322] === Symbol.for("react.memo_cache_sentinel")) {
      t115 = {
        label: "How did you hear about us",
        value: "Set to Physician Referral if blank"
      };
      $[322] = t115;
    } else {
      t115 = $[322];
    }
    let t116;
    if ($[323] !== t114) {
      t116 = <DecisionMetadata items={[t111, t112, t114, t115]} />;
      $[323] = t114;
      $[324] = t116;
    } else {
      t116 = $[324];
    }
    let t117;
    if ($[325] !== fullRegistrationReviewReasons) {
      t117 = fullRegistrationReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">{fullRegistrationReviewReasons.map(_temp19)}</ul> : null;
      $[325] = fullRegistrationReviewReasons;
      $[326] = t117;
    } else {
      t117 = $[326];
    }
    let t118;
    if ($[327] !== t116 || $[328] !== t117) {
      t118 = <div className="space-y-3">{t116}{t117}</div>;
      $[327] = t116;
      $[328] = t117;
      $[329] = t118;
    } else {
      t118 = $[329];
    }
    let t119;
    if ($[330] !== documentId) {
      t119 = <DecisionMetadata items={[{
        label: "Document ID",
        value: <CopyableContent text={documentId} textClassName="text-xs" />
      }]} />;
      $[330] = documentId;
      $[331] = t119;
    } else {
      t119 = $[331];
    }
    let t120;
    if ($[332] === Symbol.for("react.memo_cache_sentinel")) {
      t120 = <p className="text-sm font-medium">Referral Fields — fill out in order</p>;
      $[332] = t120;
    } else {
      t120 = $[332];
    }
    let t121;
    if ($[333] === Symbol.for("react.memo_cache_sentinel")) {
      t121 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1. Referring Provider</p>;
      $[333] = t121;
    } else {
      t121 = $[333];
    }
    let t122;
    if ($[334] !== opsCrossCheck.referringProviderName) {
      t122 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
      };
      $[334] = opsCrossCheck.referringProviderName;
      $[335] = t122;
    } else {
      t122 = $[335];
    }
    let t123;
    if ($[336] !== opsCrossCheck.referringProviderNpi) {
      t123 = {
        label: "NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
      };
      $[336] = opsCrossCheck.referringProviderNpi;
      $[337] = t123;
    } else {
      t123 = $[337];
    }
    let t124;
    if ($[338] !== opsCrossCheck.referringLocationAddress) {
      t124 = {
        label: "Address",
        value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
      };
      $[338] = opsCrossCheck.referringLocationAddress;
      $[339] = t124;
    } else {
      t124 = $[339];
    }
    let t125;
    if ($[340] !== t122 || $[341] !== t123 || $[342] !== t124) {
      t125 = <div className="space-y-1">{t121}<DecisionMetadata items={[t122, t123, t124]} /></div>;
      $[340] = t122;
      $[341] = t123;
      $[342] = t124;
      $[343] = t125;
    } else {
      t125 = $[343];
    }
    let t126;
    if ($[344] === Symbol.for("react.memo_cache_sentinel")) {
      t126 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2. New or Established Patient</p>;
      $[344] = t126;
    } else {
      t126 = $[344];
    }
    let t127;
    if ($[345] !== patientLabel) {
      t127 = <div className="space-y-1">{t126}<DecisionMetadata items={[{
          label: "Patient type",
          value: <CopyableContent text={patientLabel} textClassName="text-xs" />
        }]} /></div>;
      $[345] = patientLabel;
      $[346] = t127;
    } else {
      t127 = $[346];
    }
    let t128;
    if ($[347] === Symbol.for("react.memo_cache_sentinel")) {
      t128 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3. Referral Date</p>;
      $[347] = t128;
    } else {
      t128 = $[347];
    }
    let t129;
    if ($[348] !== opsCrossCheck.referralDate) {
      t129 = <div className="space-y-1">{t128}<DecisionMetadata items={[{
          label: "Referral date",
          value: <CopyableContent text={opsCrossCheck.referralDate} textClassName="text-xs" />
        }]} /></div>;
      $[348] = opsCrossCheck.referralDate;
      $[349] = t129;
    } else {
      t129 = $[349];
    }
    let t130;
    if ($[350] === Symbol.for("react.memo_cache_sentinel")) {
      t130 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4. Internal Note</p>;
      $[350] = t130;
    } else {
      t130 = $[350];
    }
    let t131;
    if ($[351] !== internalNoteValue) {
      t131 = <div className="space-y-1">{t130}<DecisionMetadata items={[{
          label: "Internal Note",
          value: <CopyableContent text={internalNoteValue} textClassName="text-xs" />
        }]} /></div>;
      $[351] = internalNoteValue;
      $[352] = t131;
    } else {
      t131 = $[352];
    }
    let t132;
    if ($[353] !== t125 || $[354] !== t127 || $[355] !== t129 || $[356] !== t131) {
      t132 = <div className="space-y-3 rounded-md border p-3">{t120}{t125}{t127}{t129}{t131}</div>;
      $[353] = t125;
      $[354] = t127;
      $[355] = t129;
      $[356] = t131;
      $[357] = t132;
    } else {
      t132 = $[357];
    }
    let t133;
    if ($[358] !== t119 || $[359] !== t132) {
      t133 = <div className="space-y-3">{t119}{t132}</div>;
      $[358] = t119;
      $[359] = t132;
      $[360] = t133;
    } else {
      t133 = $[360];
    }
    let t134;
    if ($[361] !== documentUploadLabels.length) {
      t134 = <DecisionMetadata items={[{
        label: "Documents",
        value: documentUploadLabels.length
      }]} />;
      $[361] = documentUploadLabels.length;
      $[362] = t134;
    } else {
      t134 = $[362];
    }
    let t135;
    if ($[363] === Symbol.for("react.memo_cache_sentinel")) {
      t135 = <p className="text-sm font-medium">Expected Athena uploads</p>;
      $[363] = t135;
    } else {
      t135 = $[363];
    }
    let t136;
    if ($[364] !== documentUploadLabels) {
      t136 = <div className="space-y-1">{t135}{documentUploadLabels.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{documentUploadLabels.map(_temp20)}</ul> : <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>}</div>;
      $[364] = documentUploadLabels;
      $[365] = t136;
    } else {
      t136 = $[365];
    }
    let t137;
    if ($[366] !== t134 || $[367] !== t136) {
      t137 = <div className="space-y-3">{t134}{t136}</div>;
      $[366] = t134;
      $[367] = t136;
      $[368] = t137;
    } else {
      t137 = $[368];
    }
    let t138;
    if ($[369] === Symbol.for("react.memo_cache_sentinel")) {
      t138 = <p className="text-sm font-medium">Bucket</p>;
      $[369] = t138;
    } else {
      t138 = $[369];
    }
    const t139 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? NO_CONFIDENT_BUCKET_SUGGESTION : SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel;
    let t140;
    if ($[370] !== t139) {
      t140 = {
        label: "Suggested bucket",
        value: <CopyableContent text={t139} />
      };
      $[370] = t139;
      $[371] = t140;
    } else {
      t140 = $[371];
    }
    const t141 = hasMissingInformation ? "Required" : "No";
    let t142;
    if ($[372] !== t141) {
      t142 = {
        label: "Missing information",
        value: t141
      };
      $[372] = t141;
      $[373] = t142;
    } else {
      t142 = $[373];
    }
    const t143 = authRequestUploaded ? "Uploaded" : "Not required";
    let t144;
    if ($[374] !== t143) {
      t144 = {
        label: "Authorization request",
        value: t143
      };
      $[374] = t143;
      $[375] = t144;
    } else {
      t144 = $[375];
    }
    let t145;
    if ($[376] !== t140 || $[377] !== t142 || $[378] !== t144) {
      t145 = <DecisionMetadata items={[t140, t142, t144]} />;
      $[376] = t140;
      $[377] = t142;
      $[378] = t144;
      $[379] = t145;
    } else {
      t145 = $[379];
    }
    let t146;
    if ($[380] !== decisionTrace) {
      t146 = decisionTrace.length > 0 ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">How this was determined</summary><ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">{decisionTrace.map(_temp21)}</ul></details> : null;
      $[380] = decisionTrace;
      $[381] = t146;
    } else {
      t146 = $[381];
    }
    let t147;
    if ($[382] !== t145 || $[383] !== t146) {
      t147 = <div className="space-y-2 rounded-md border p-3">{t138}{t145}{t146}</div>;
      $[382] = t145;
      $[383] = t146;
      $[384] = t147;
    } else {
      t147 = $[384];
    }
    let t148;
    if ($[385] !== notesComplete || $[386] !== resolvedReceiving.routedToSscardio || $[387] !== task.id || $[388] !== task.referral || $[389] !== task.type || $[390] !== workflowStillProcessing) {
      t148 = !resolvedReceiving.routedToSscardio && !workflowStillProcessing && notesComplete && task.type === TaskType.REFERRAL && task.referral && <TaskSchedulingRecommendation taskId={task.id} referral={task.referral} preferAgenticReceivingProvider={true} />;
      $[385] = notesComplete;
      $[386] = resolvedReceiving.routedToSscardio;
      $[387] = task.id;
      $[388] = task.referral;
      $[389] = task.type;
      $[390] = workflowStillProcessing;
      $[391] = t148;
    } else {
      t148 = $[391];
    }
    let t149;
    if ($[392] === Symbol.for("react.memo_cache_sentinel")) {
      t149 = <p className="text-sm font-medium">Action Notes</p>;
      $[392] = t149;
    } else {
      t149 = $[392];
    }
    let t150;
    if ($[393] !== actionNote) {
      t150 = <div className="space-y-2 rounded-md border p-3">{t149}<DecisionMetadata items={[{
          label: "Action Notes",
          value: <CopyableContent text={actionNote} textClassName="text-xs" />
        }]} /></div>;
      $[393] = actionNote;
      $[394] = t150;
    } else {
      t150 = $[394];
    }
    let t151;
    if ($[395] !== t147 || $[396] !== t148 || $[397] !== t150) {
      t151 = <div className="space-y-3">{t147}{t148}{t150}</div>;
      $[395] = t147;
      $[396] = t148;
      $[397] = t150;
      $[398] = t151;
    } else {
      t151 = $[398];
    }
    steps = [{
      number: 1,
      title: "Locate the Document in the Patient Chart",
      status: ensurePatientStatus,
      summary: "Copy and search by the document ID in Referral.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t31
    }, {
      number: 2,
      title: "Verify the provider and location",
      status: assignmentConfirmationStatus,
      taskStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      summary: usesRetiredUsualProviderRule ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history." : assignmentConfirmationStatus === "needs_action" ? "Provider and location require manual review before downstream referral work." : "Verify we selected the correct receiving provider and location.",
      content: t47,
      continuation: receivingAssignmentApproval ? {
        instruction: "After confirming the provider and location in Athena, continue the workflow.",
        control: <TprReceivingAssignmentContinueButton task={task} />
      } : undefined
    }, {
      number: 3,
      title: "Update demographics and care team",
      status: demographicsStatus,
      taskStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: t71
    }, {
      number: 4,
      title: "Insurance",
      status: resolveConsolidatedStatus([coverageStatus, athenaEligibilityStatus, networkStatus]),
      taskStepKeys: [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE, StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY, StepKey.SYNC_ATHENA_COVERAGES, StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS],
      summary: coverageApproval ? coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing." : coverageReadback?.primary.selfPay ? "Self Pay \u2014 no network or eligibility checks apply." : insuranceRequirements ? `In network: ${inNetworkDisplay}. Athena eligibility: ${insuranceStatusDisplay}.` : coverageReadbackComplete ? "Coverage confirmed. Waiting on Athena eligibility and network/requirements decisions." : assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : "Waiting for the coverage write, Athena eligibility, and network/requirements decisions.",
      content: t92,
      continuation: coverageApproval ? {
        instruction: "After verifying the coverage sequence in Athena, continue the workflow.",
        control: <TprCoverageContinueButton task={task} />
      } : undefined
    }, {
      number: 5,
      title: "Referral Authorization",
      status: resolveConsolidatedStatus([pcpFaxStatus, authorizationStatus]),
      taskStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX, StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary: t93,
      content: t106,
      continuation: opsKind === "pcp_referral_fax" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After sending the outbound PCP referral fax from Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="PCP referral fax confirmed" successDescription="The workflow is continuing with authorization." />
      } : opsKind === "authorization_review" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After reviewing the Athena referral authorization object, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Authorization reviewed" successDescription="The workflow is continuing with the remaining referral steps." />
      } : opsKind === "manual_authorization" ? {
        instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
        control: <TprOpsContinueButton task={task} successTitle="Manual authorization recorded" successDescription="The workflow is continuing with the remaining referral steps." />
      } : undefined
    }, {
      number: 6,
      title: "Outbound Faxes",
      status: outboundFaxStatus,
      taskStepKeys: [StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "auth_request_upload" ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically." : outboundFaxStatus === "not_needed" ? "No outbound fax is required for this referral." : "Send the required outbound fax(es) from Athena, then upload where applicable.",
      content: t110,
      continuation: opsKind === "auth_request_upload" ? {
        instruction: "After exporting the chart in Athena, upload the request letter.",
        control: <TprOpsContinueButton task={task} label="Upload request letter" pendingLabel="Uploading..." successTitle="Request letter queued" successDescription="The workflow will write the pending authorization and upload the letter." />
      } : undefined
    }, {
      number: 7,
      title: "Review Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t118
    }, {
      number: 8,
      title: "Process referral document",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA]),
      summary: "Referral metadata is written to Athena in the order ops should verify it.",
      taskStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA],
      content: t133
    }, {
      number: 9,
      title: "Check document upload",
      status: taskStepStatus(taskSteps, [StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: documentUploadLabels.length ? "Confirm the expected documents were uploaded to Athena." : "No documents were selected for upload.",
      taskStepKeys: [StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: t137
    }, {
      number: 10,
      title: "Verify in Quickview",
      status: dispositionStatus === "failed" ? "not_run" : notesComplete ? "verify" : "not_run",
      taskStepKeys: [],
      summary: notesComplete ? "Confirm the note above appears correctly on the patient's Quickview tab in Athena." : "Waiting for the final note to be written before this can be verified."
    }, {
      number: 11,
      title: "Bucket Assignment",
      status: resolveConsolidatedStatus([closeDocumentStatus, dispositionStatus === "failed" ? "not_run" : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete) ? "needs_action" : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX])]),
      taskStepKeys: [StepKey.TPR_CLOSE_DOCUMENT, StepKey.REFERRAL_DISPOSITION, StepKey.SCHEDULING_RECOMMENDATION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "close_document" ? opsApproval?.metadata?.instruction ?? "There is an upcoming appointment within 90 days. Close the document in Athena, then continue." : hasMissingInformation && !missingInfoUploadComplete ? "Waiting for the missing-information document to be prepared." : referralAuthNeedsPendingUpload && authRequestUploaded ? "Confirm the authorization-request letter on the chart, route, and complete." : hasMissingInformation ? "Send the missing-information document, route the referral, and complete only after all manual actions are done." : "Confirm the recommended destination bucket \u2014 SS Cardio, Biltmore, or Close \u2014 and complete the task.",
      content: t151,
      continuation: opsKind === "close_document" ? {
        instruction: "After closing the document in Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Document close confirmed" successDescription="The workflow is continuing with remaining follow-through." />
      } : verificationActionStepNumber === 11 ? {
        instruction: `After completing the manual follow-through in Athena and assigning ${bucketLabel}, continue to complete the task.`,
        control: <VerificationButtons task={task} continueOnly={true} />
      } : undefined
    }];
    let t152;
    if ($[399] !== taskSteps) {
      t152 = taskSteps.find(_temp22);
      $[399] = taskSteps;
      $[400] = t152;
    } else {
      t152 = $[400];
    }
    const failedTaskStep = t152;
    failedStepCard = failedTaskStep ? steps.find(step_6 => step_6.taskStepKeys.some(stepKey => stepKey === failedTaskStep.stepKey)) : undefined;
    t3 = failedTaskStep && failedStepCard ? failedStepCard.taskStepKeys.find(stepKey_0 => stepKey_0 === failedTaskStep.stepKey) ?? null : null;
    $[6] = actionNote;
    $[7] = bucketLabel;
    $[8] = coverageReadback;
    $[9] = decisionTrace;
    $[10] = documentId;
    $[11] = documentUploadLabels;
    $[12] = followThroughEligible;
    $[13] = hasMissingInformation;
    $[14] = insuranceRequirements;
    $[15] = insuranceStatusDisplay;
    $[16] = internalNoteValue;
    $[17] = isPrimaryCareProvider;
    $[18] = isProviderFirst;
    $[19] = opsCrossCheck.diagnosis;
    $[20] = opsCrossCheck.emergencyContactName;
    $[21] = opsCrossCheck.emergencyContactPhoneNumber;
    $[22] = opsCrossCheck.emergencyContactRelationship;
    $[23] = opsCrossCheck.isSpecialCaseInsurance;
    $[24] = opsCrossCheck.patientDateOfBirth;
    $[25] = opsCrossCheck.patientEthnicity;
    $[26] = opsCrossCheck.patientFaxNumber;
    $[27] = opsCrossCheck.patientHomeAddress;
    $[28] = opsCrossCheck.patientLanguage;
    $[29] = opsCrossCheck.patientName;
    $[30] = opsCrossCheck.patientPhoneNumber;
    $[31] = opsCrossCheck.patientRace;
    $[32] = opsCrossCheck.primaryPlanLabel;
    $[33] = opsCrossCheck.primarySubscriberId;
    $[34] = opsCrossCheck.receivingProviderName;
    $[35] = opsCrossCheck.receivingProviderNpi;
    $[36] = opsCrossCheck.referralDate;
    $[37] = opsCrossCheck.referringLocationAddress;
    $[38] = opsCrossCheck.referringProviderName;
    $[39] = opsCrossCheck.referringProviderNpi;
    $[40] = opsCrossCheck.referringProviderSpecialty;
    $[41] = patientDisplayId;
    $[42] = patientEhrId;
    $[43] = patientLabel;
    $[44] = pendingApproval;
    $[45] = primaryCareProviderAlreadyPresent;
    $[46] = privacyNoticeOnFile;
    $[47] = providerSelectionEvidence;
    $[48] = receivingAssignmentConfirmed;
    $[49] = referralAuthorization;
    $[50] = referralId;
    $[51] = resolvedReceiving.advisories;
    $[52] = resolvedReceiving.flags;
    $[53] = resolvedReceiving.locationLabel;
    $[54] = resolvedReceiving.providerLabel;
    $[55] = resolvedReceiving.providerNpi;
    $[56] = resolvedReceiving.providerWriteSafe;
    $[57] = resolvedReceiving.routedToSscardio;
    $[58] = resolvedReceiving.usualProviderWriteSafe;
    $[59] = task;
    $[60] = taskSteps;
    $[61] = unresolvedUpcomingProviders;
    $[62] = upcomingAppointmentProviderSummary;
    $[63] = workflowStillProcessing;
    $[64] = coverageApproval;
    $[65] = failedStepCard;
    $[66] = opsApproval;
    $[67] = steps;
    $[68] = t3;
    $[69] = waitingForAssociatedReferral;
  } else {
    coverageApproval = $[64];
    failedStepCard = $[65];
    opsApproval = $[66];
    steps = $[67];
    t3 = $[68];
    waitingForAssociatedReferral = $[69];
  }
  const failedStepKey = t3;
  let t4;
  if ($[401] !== coverageApproval || $[402] !== failedStepCard?.number || $[403] !== opsApproval || $[404] !== steps) {
    t4 = coverageApproval ? steps.filter(_temp23) : opsApproval ? steps.filter(_temp24) : steps.filter(step_9 => step_9.number === failedStepCard?.number || step_9.status === "needs_action" || step_9.status === "failed" || step_9.status === "verify");
    $[401] = coverageApproval;
    $[402] = failedStepCard?.number;
    $[403] = opsApproval;
    $[404] = steps;
    $[405] = t4;
  } else {
    t4 = $[405];
  }
  const nextSteps = t4;
  const actionsRemainingVariant = failedStepCard || nextSteps.some(_temp25) ? "destructive" : nextSteps.length || waitingForAssociatedReferral ? "warn" : workflowStillProcessing ? "outline" : "success";
  let t5;
  if ($[406] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ListChecks />;
    $[406] = t5;
  } else {
    t5 = $[406];
  }
  let t6;
  if ($[407] !== failedStepCard?.number || $[408] !== nextSteps || $[409] !== waitingForAssociatedReferral || $[410] !== workflowStillProcessing) {
    t6 = waitingForAssociatedReferral ? <p className="text-sm">Waiting for a matching Referral for this patient. Standalone Referral Auth holds until a Referral in the two-day window finishes syncing.</p> : nextSteps.length ? <ol className="space-y-2 text-sm">{nextSteps.map(step_11 => <li key={step_11.number}><strong>Step {step_11.number}: {step_11.title}</strong><span className="block text-muted-foreground">{step_11.number === failedStepCard?.number ? "Complete this step manually, then continue from its card." : step_11.summary}</span></li>)}</ol> : <p className="text-sm">{workflowStillProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : "No manual actions remain."}</p>;
    $[407] = failedStepCard?.number;
    $[408] = nextSteps;
    $[409] = waitingForAssociatedReferral;
    $[410] = workflowStillProcessing;
    $[411] = t6;
  } else {
    t6 = $[411];
  }
  let t7;
  if ($[412] !== actionsRemainingVariant || $[413] !== t6) {
    t7 = <ValerieCard title="Actions remaining" icon={t5} variant={actionsRemainingVariant} contentClassName="space-y-4">{t6}</ValerieCard>;
    $[412] = actionsRemainingVariant;
    $[413] = t6;
    $[414] = t7;
  } else {
    t7 = $[414];
  }
  let t8;
  if ($[415] !== failedStepCard?.number || $[416] !== failedStepKey || $[417] !== steps || $[418] !== task) {
    t8 = steps.map(step_12 => <TimelineStepCard key={step_12.number} task={task} step={step_12} failedStepKey={step_12.number === failedStepCard?.number ? failedStepKey : null} />);
    $[415] = failedStepCard?.number;
    $[416] = failedStepKey;
    $[417] = steps;
    $[418] = task;
    $[419] = t8;
  } else {
    t8 = $[419];
  }
  let t9;
  if ($[420] !== t7 || $[421] !== t8) {
    t9 = <div className="space-y-8 pb-8">{t7}{t8}</div>;
    $[420] = t7;
    $[421] = t8;
    $[422] = t9;
  } else {
    t9 = $[422];
  }
  return t9;
}
function _temp25(step_10) {
  return step_10.status === "failed";
}
function _temp24(step_8) {
  return step_8.continuation;
}
function _temp23(step_7) {
  return step_7.number === 4;
}
function _temp22(step_5) {
  return step_5.status === TaskStepStatus.FAILED;
}
function _temp21(entry, index) {
  return <li key={`${entry.label}-${index}`}>{entry.label} <strong>{entry.outcome}</strong></li>;
}
function _temp20(label) {
  return <li key={label}>{label}</li>;
}
function _temp19(reason_4) {
  return <li key={reason_4}>{reason_4}</li>;
}
function _temp18(reason_3) {
  return <li key={reason_3}>{reason_3}</li>;
}
function _temp17(reason_2) {
  return <li key={reason_2}>{reason_2}</li>;
}
function _temp16(provider) {
  return <Badge key={provider.providerEhrId ?? provider.label} variant="outline">{provider.label}{provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}</Badge>;
}
function _temp15(reason_1) {
  return <li key={reason_1}>{reason_1}</li>;
}
function _temp14(advisory) {
  return <li key={advisory}>{advisory}</li>;
}
function _temp13(coverage) {
  return <p key={coverage.insuranceId}><strong>{coverage.casePolicy ? "Case policy" : coverage.sequence === 1 ? "Primary" : coverage.sequence === 2 ? "Secondary" : `Sequence ${coverage.sequence ?? "N/A"}`}:</strong>{" "}{coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />{coverage.subscriberId ? <>{" "}· Subscriber <CopyableContent text={coverage.subscriberId} /></> : null}</p>;
}
function _temp12(reason_0) {
  return reason_0 !== null;
}
function _temp11(reason) {
  return reason !== null;
}
function _temp10(file) {
  return file.type === FileType.INBOUND;
}
function _temp9(statuses) {
  if (statuses.includes("failed")) {
    return "failed";
  }
  if (statuses.includes("needs_action")) {
    return "needs_action";
  }
  if (statuses.includes("verify")) {
    return "verify";
  }
  if (statuses.includes("waiting")) {
    return "waiting";
  }
  if (statuses.includes("verified")) {
    return "verified";
  }
  if (statuses.includes("automated")) {
    return "automated";
  }
  if (statuses.includes("not_run")) {
    return "not_run";
  }
  return "not_needed";
}
function _temp8(step_4) {
  return step_4.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step_4.status === TaskStepStatus.COMPLETED;
}
function _temp7(step_3) {
  return step_3.stepKey === StepKey.REFERRAL_DISPOSITION && step_3.status === TaskStepStatus.COMPLETED;
}
function _temp6(step_2) {
  return step_2.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD && step_2.status === TaskStepStatus.COMPLETED && step_2.output?.uploaded === true;
}
function _temp5(step_1) {
  return step_1.stepKey === StepKey.READ_ATHENA_COVERAGE && step_1.status === TaskStepStatus.COMPLETED;
}
function _temp4(step_0) {
  return step_0.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step_0.status === TaskStepStatus.PAUSED;
}
function _temp3(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT;
}
interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  /** Whether the referring provider is already on the Athena care team as PRIMARY_CARE_PROVIDER —
   * suppresses the redundant "PCP" line in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's four values: "closed" | "sscardio" | "biltmorecardreferral" | "biltmorecardiosched" */
  bucketLabel: string;
  actionNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploadLabels: string[];
  privacyNoticeOnFile: boolean | null;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    trace: string[];
    flags: string[];
    /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) — always shown,
     * unlike `flags`, which only renders once the assignment already needs manual review. */
    advisories: string[];
    routedToSscardio: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
}
export function TprReferralInstructions(t0) {
  const $ = _c(414);
  const {
    view,
    referral,
    task,
    athenaData,
    patientEhrId,
    patientLabel,
    upcomingAppointments,
    pastAppointments,
    documentId,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    bucketLabel,
    actionNote,
    decisionTrace,
    flags,
    eligibilityDisplay,
    documentUploadLabels,
    privacyNoticeOnFile,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    resolvedReceiving
  } = t0;
  let t1;
  if ($[0] !== referral.referringLocation?.address) {
    t1 = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
    $[0] = referral.referringLocation?.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringLocationAddress = t1;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  let t2;
  if ($[2] !== referral.referringProvider) {
    t2 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : NOT_AVAILABLE;
    $[2] = referral.referringProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const referringProviderName = t2;
  let t3;
  if ($[4] !== referral.receivingProvider) {
    t3 = referral.receivingProvider ? getPractitionerNameWithCredentials(referral.receivingProvider) : NOT_AVAILABLE;
    $[4] = referral.receivingProvider;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const receivingProviderName = t3;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  let t4;
  if ($[6] !== referral.primaryCoverage) {
    t4 = getCoverageSummary(referral.primaryCoverage);
    $[6] = referral.primaryCoverage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const primaryCoverageSummary = t4;
  let t5;
  if ($[8] !== referral.secondaryCoverage) {
    t5 = getCoverageSummary(referral.secondaryCoverage);
    $[8] = referral.secondaryCoverage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const secondaryCoverageSummary = t5;
  let t6;
  if ($[10] !== referral.dateOfReferral) {
    t6 = formatDateMmDdYyyy(referral.dateOfReferral) || NOT_AVAILABLE;
    $[10] = referral.dateOfReferral;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const dateOfReferral = t6;
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  const internalNoteValue = athenaData?.document?.internalNote || NOT_AVAILABLE;
  let T0;
  let dataSourceEntries;
  let hasMissingInformation;
  let missingFields;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t7;
  let t8;
  let t9;
  if ($[12] !== actionNote || $[13] !== athenaData?.coverages || $[14] !== athenaData?.patientDetails || $[15] !== bucketLabel || $[16] !== coverageReadback || $[17] !== dateOfReferral || $[18] !== decisionTrace || $[19] !== documentId || $[20] !== documentUploadLabels || $[21] !== eligibilityDisplay || $[22] !== emergencyContactName || $[23] !== emergencyContactPhoneNumber || $[24] !== emergencyContactRelationship || $[25] !== followThroughEligible || $[26] !== insuranceRequirements || $[27] !== internalNoteValue || $[28] !== isPrimaryCareProvider || $[29] !== pastAppointments || $[30] !== patient || $[31] !== patientEhrId || $[32] !== patientLabel || $[33] !== pendingApproval || $[34] !== primaryCareProviderAlreadyPresent || $[35] !== primaryCoverageSummary || $[36] !== privacyNoticeOnFile || $[37] !== receivingAssignmentConfirmed || $[38] !== receivingProviderName || $[39] !== referral.diagnosisList || $[40] !== referral.id || $[41] !== referral.primaryCoverage || $[42] !== referral.reasonForReferral || $[43] !== referral.receivingLocation?.name || $[44] !== referral.receivingProvider || $[45] !== referral.referringLocation?.name || $[46] !== referral.referringProvider || $[47] !== referralAuthorization || $[48] !== referringLocationAddress || $[49] !== referringProviderName || $[50] !== referringProviderSpecialty || $[51] !== resolvedReceiving || $[52] !== secondaryCoverageSummary || $[53] !== task || $[54] !== taskSteps || $[55] !== unresolvedUpcomingProviders || $[56] !== upcomingAppointmentProviderSummary || $[57] !== upcomingAppointments || $[58] !== view) {
    t25 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
      const syncFailed = task.status === TaskStatus.FAILED;
      const insuranceStatusDisplay = coverageReadback?.primary.selfPay ? "Self Pay" : referral.primaryCoverage ? eligibilityDisplay : "Missing Insurance";
      const chartPatient = athenaData?.patientDetails ?? null;
      let t26;
      if ($[82] !== patient) {
        t26 = patient && getPatientPhoneNumber(patient)?.trim() || null;
        $[82] = patient;
        $[83] = t26;
      } else {
        t26 = $[83];
      }
      const referralPhone = t26;
      let t27;
      if ($[84] !== chartPatient) {
        t27 = chartPatient && getPatientPhoneNumber(chartPatient)?.trim() || null;
        $[84] = chartPatient;
        $[85] = t27;
      } else {
        t27 = $[85];
      }
      const chartPhone = t27;
      let chartAddressValue;
      let phoneDataSourceEntry;
      let referralAddressValue;
      let t28;
      let t29;
      let t30;
      if ($[86] !== athenaData?.coverages || $[87] !== chartPatient || $[88] !== chartPhone || $[89] !== patient || $[90] !== referral.primaryCoverage || $[91] !== referralPhone) {
        const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
        const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort(_temp26)[0];
        const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
        referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
        chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
        let t31;
        if ($[98] !== chartPhone || $[99] !== referralPhone) {
          t31 = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
          $[98] = chartPhone;
          $[99] = referralPhone;
          $[100] = t31;
        } else {
          t31 = $[100];
        }
        phoneDataSourceEntry = t31;
        t28 = phoneDataSourceEntry;
        t29 = resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue);
        t30 = resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue);
        $[86] = athenaData?.coverages;
        $[87] = chartPatient;
        $[88] = chartPhone;
        $[89] = patient;
        $[90] = referral.primaryCoverage;
        $[91] = referralPhone;
        $[92] = chartAddressValue;
        $[93] = phoneDataSourceEntry;
        $[94] = referralAddressValue;
        $[95] = t28;
        $[96] = t29;
        $[97] = t30;
      } else {
        chartAddressValue = $[92];
        phoneDataSourceEntry = $[93];
        referralAddressValue = $[94];
        t28 = $[95];
        t29 = $[96];
        t30 = $[97];
      }
      let t31;
      if ($[101] !== t28 || $[102] !== t29 || $[103] !== t30) {
        t31 = [t28, t29, t30];
        $[101] = t28;
        $[102] = t29;
        $[103] = t30;
        $[104] = t31;
      } else {
        t31 = $[104];
      }
      dataSourceEntries = t31;
      const isCamelbackReferral = !resolvedReceiving.routedToSscardio;
      let t32;
      if ($[105] !== isCamelbackReferral || $[106] !== phoneDataSourceEntry) {
        t32 = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
        $[105] = isCamelbackReferral;
        $[106] = phoneDataSourceEntry;
        $[107] = t32;
      } else {
        t32 = $[107];
      }
      missingFields = t32;
      hasMissingInformation = missingFields.length > 0;
      let t33;
      if ($[108] !== patient) {
        t33 = getPatientDisplayName(patient);
        $[108] = patient;
        $[109] = t33;
      } else {
        t33 = $[109];
      }
      const patientDisplayName = t33;
      const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
      let t34;
      if ($[110] !== referral.diagnosisList || $[111] !== referral.reasonForReferral) {
        t34 = [referral.reasonForReferral, ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map(_temp27)].filter(_temp28).filter(_temp29).join(", ") || NOT_AVAILABLE;
        $[110] = referral.diagnosisList;
        $[111] = referral.reasonForReferral;
        $[112] = t34;
      } else {
        t34 = $[112];
      }
      const diagnosis = t34;
      let t35;
      if ($[113] !== patient) {
        t35 = patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE;
        $[113] = patient;
        $[114] = t35;
      } else {
        t35 = $[114];
      }
      let t36;
      if ($[115] !== patient?.dateOfBirth) {
        t36 = formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE;
        $[115] = patient?.dateOfBirth;
        $[116] = t36;
      } else {
        t36 = $[116];
      }
      const t37 = chartPhone ?? referralPhone ?? NOT_AVAILABLE;
      let t38;
      if ($[117] !== patient) {
        t38 = patient && getPatientFaxNumber(patient) || NOT_AVAILABLE;
        $[117] = patient;
        $[118] = t38;
      } else {
        t38 = $[118];
      }
      const t39 = patient?.ethnicity || NOT_AVAILABLE;
      const t40 = patient?.race || NOT_AVAILABLE;
      const t41 = patient?.language || NOT_AVAILABLE;
      const t42 = chartAddressValue ?? referralAddressValue ?? NOT_AVAILABLE;
      const t43 = referral.receivingProvider?.npiNumber || NOT_AVAILABLE;
      const t44 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      const t45 = referral.referringLocation?.name || NOT_AVAILABLE;
      let t46;
      if ($[119] !== referral.primaryCoverage) {
        t46 = referral.primaryCoverage ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : "Self Pay";
        $[119] = referral.primaryCoverage;
        $[120] = t46;
      } else {
        t46 = $[120];
      }
      const t47 = referral.primaryCoverage?.subscriberId || NOT_AVAILABLE;
      const t48 = referral.primaryCoverage?.payer;
      let t49;
      if ($[121] !== t48) {
        t49 = isTprSpecialCaseInsurance(t48);
        $[121] = t48;
        $[122] = t49;
      } else {
        t49 = $[122];
      }
      let t50;
      if ($[123] !== dateOfReferral || $[124] !== diagnosis || $[125] !== emergencyContactName || $[126] !== emergencyContactPhoneNumber || $[127] !== emergencyContactRelationship || $[128] !== receivingProviderName || $[129] !== referringLocationAddress || $[130] !== referringProviderName || $[131] !== referringProviderSpecialty || $[132] !== t35 || $[133] !== t36 || $[134] !== t37 || $[135] !== t38 || $[136] !== t39 || $[137] !== t40 || $[138] !== t41 || $[139] !== t42 || $[140] !== t43 || $[141] !== t44 || $[142] !== t45 || $[143] !== t46 || $[144] !== t47 || $[145] !== t49) {
        t50 = {
          patientName: t35,
          patientDateOfBirth: t36,
          patientPhoneNumber: t37,
          patientFaxNumber: t38,
          patientEthnicity: t39,
          patientRace: t40,
          patientLanguage: t41,
          patientHomeAddress: t42,
          receivingProviderName,
          receivingProviderNpi: t43,
          referringProviderName,
          referringProviderNpi: t44,
          referringProviderSpecialty,
          referringLocationName: t45,
          referringLocationAddress,
          emergencyContactName,
          emergencyContactPhoneNumber,
          emergencyContactRelationship,
          referralDate: dateOfReferral,
          primaryPlanLabel: t46,
          primarySubscriberId: t47,
          isSpecialCaseInsurance: t49,
          diagnosis
        };
        $[123] = dateOfReferral;
        $[124] = diagnosis;
        $[125] = emergencyContactName;
        $[126] = emergencyContactPhoneNumber;
        $[127] = emergencyContactRelationship;
        $[128] = receivingProviderName;
        $[129] = referringLocationAddress;
        $[130] = referringProviderName;
        $[131] = referringProviderSpecialty;
        $[132] = t35;
        $[133] = t36;
        $[134] = t37;
        $[135] = t38;
        $[136] = t39;
        $[137] = t40;
        $[138] = t41;
        $[139] = t42;
        $[140] = t43;
        $[141] = t44;
        $[142] = t45;
        $[143] = t46;
        $[144] = t47;
        $[145] = t49;
        $[146] = t50;
      } else {
        t50 = $[146];
      }
      const opsCrossCheck = t50;
      if (view === "steps") {
        let t51;
        if ($[147] !== actionNote || $[148] !== bucketLabel || $[149] !== coverageReadback || $[150] !== decisionTrace || $[151] !== documentId || $[152] !== documentUploadLabels || $[153] !== followThroughEligible || $[154] !== hasMissingInformation || $[155] !== insuranceRequirements || $[156] !== insuranceStatusDisplay || $[157] !== internalNoteValue || $[158] !== isPrimaryCareProvider || $[159] !== opsCrossCheck || $[160] !== pastAppointments || $[161] !== patient.displayId || $[162] !== patientEhrId || $[163] !== patientLabel || $[164] !== pendingApproval || $[165] !== primaryCareProviderAlreadyPresent || $[166] !== privacyNoticeOnFile || $[167] !== receivingAssignmentConfirmed || $[168] !== referral.id || $[169] !== referralAuthorization || $[170] !== resolvedReceiving || $[171] !== task || $[172] !== taskSteps || $[173] !== unresolvedUpcomingProviders || $[174] !== upcomingAppointmentProviderSummary || $[175] !== upcomingAppointments) {
          t51 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto"><TprSyncTimeline task={task} referralId={referral.id} taskSteps={taskSteps} pendingApproval={pendingApproval} receivingAssignmentConfirmed={receivingAssignmentConfirmed} followThroughEligible={followThroughEligible} coverageReadback={coverageReadback} insuranceRequirements={insuranceRequirements} referralAuthorization={referralAuthorization} resolvedReceiving={resolvedReceiving} patientLabel={patientLabel} patientEhrId={patientEhrId} documentId={documentId} bucketLabel={bucketLabel} internalNoteValue={internalNoteValue} actionNote={actionNote} decisionTrace={decisionTrace} insuranceStatusDisplay={insuranceStatusDisplay} hasMissingInformation={hasMissingInformation} unresolvedUpcomingProviders={unresolvedUpcomingProviders} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} upcomingAppointments={upcomingAppointments} pastAppointments={pastAppointments} documentUploadLabels={documentUploadLabels} privacyNoticeOnFile={privacyNoticeOnFile} isPrimaryCareProvider={isPrimaryCareProvider} primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent} patientDisplayId={patient.displayId} opsCrossCheck={opsCrossCheck} /></div>;
          $[147] = actionNote;
          $[148] = bucketLabel;
          $[149] = coverageReadback;
          $[150] = decisionTrace;
          $[151] = documentId;
          $[152] = documentUploadLabels;
          $[153] = followThroughEligible;
          $[154] = hasMissingInformation;
          $[155] = insuranceRequirements;
          $[156] = insuranceStatusDisplay;
          $[157] = internalNoteValue;
          $[158] = isPrimaryCareProvider;
          $[159] = opsCrossCheck;
          $[160] = pastAppointments;
          $[161] = patient.displayId;
          $[162] = patientEhrId;
          $[163] = patientLabel;
          $[164] = pendingApproval;
          $[165] = primaryCareProviderAlreadyPresent;
          $[166] = privacyNoticeOnFile;
          $[167] = receivingAssignmentConfirmed;
          $[168] = referral.id;
          $[169] = referralAuthorization;
          $[170] = resolvedReceiving;
          $[171] = task;
          $[172] = taskSteps;
          $[173] = unresolvedUpcomingProviders;
          $[174] = upcomingAppointmentProviderSummary;
          $[175] = upcomingAppointments;
          $[176] = t51;
        } else {
          t51 = $[176];
        }
        t25 = t51;
        break bb0;
      }
      t24 = "flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto";
      T0 = EhrInstructionsCard;
      t23 = "Detailed Athena verification reference";
      t16 = "text-sm text-muted-foreground space-y-6";
      if ($[177] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <h3 className="text-md font-semibold text-center text-foreground">TPR Biltmore Cardiology Sync Verification</h3>;
        $[177] = t17;
      } else {
        t17 = $[177];
      }
      const t51 = syncFailed ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing." : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing.";
      if ($[178] !== t51) {
        t18 = <p className="text-xs text-center">{t51}</p>;
        $[178] = t51;
        $[179] = t18;
      } else {
        t18 = $[179];
      }
      if ($[180] !== task.id) {
        t19 = <SyncIncompleteActions taskId={task.id} />;
        $[180] = task.id;
        $[181] = t19;
      } else {
        t19 = $[181];
      }
      let t52;
      if ($[182] === Symbol.for("react.memo_cache_sentinel")) {
        t52 = <strong>Camelback Referral:</strong>;
        $[182] = t52;
      } else {
        t52 = $[182];
      }
      const t53 = isCamelbackReferral ? "Yes" : "No";
      let t54;
      if ($[183] !== t53) {
        t54 = <p>{t52} {t53}</p>;
        $[183] = t53;
        $[184] = t54;
      } else {
        t54 = $[184];
      }
      let t55;
      if ($[185] === Symbol.for("react.memo_cache_sentinel")) {
        t55 = <strong>Patient:</strong>;
        $[185] = t55;
      } else {
        t55 = $[185];
      }
      let t56;
      if ($[186] !== patientDisplayName) {
        t56 = <CopyableContent text={patientDisplayName} />;
        $[186] = patientDisplayName;
        $[187] = t56;
      } else {
        t56 = $[187];
      }
      let t57;
      if ($[188] !== patientLabel || $[189] !== t56) {
        t57 = <p>{t55} {t56} ({patientLabel})</p>;
        $[188] = patientLabel;
        $[189] = t56;
        $[190] = t57;
      } else {
        t57 = $[190];
      }
      let t58;
      if ($[191] === Symbol.for("react.memo_cache_sentinel")) {
        t58 = <strong>Receiving Provider (as extracted):</strong>;
        $[191] = t58;
      } else {
        t58 = $[191];
      }
      let t59;
      if ($[192] !== referral.receivingProvider) {
        t59 = <ProviderCopyFields provider={referral.receivingProvider} />;
        $[192] = referral.receivingProvider;
        $[193] = t59;
      } else {
        t59 = $[193];
      }
      let t60;
      if ($[194] === Symbol.for("react.memo_cache_sentinel")) {
        t60 = <strong>Location:</strong>;
        $[194] = t60;
      } else {
        t60 = $[194];
      }
      let t61;
      if ($[195] !== receivingLocationName) {
        t61 = <CopyableContent text={receivingLocationName} />;
        $[195] = receivingLocationName;
        $[196] = t61;
      } else {
        t61 = $[196];
      }
      let t62;
      if ($[197] !== t59 || $[198] !== t61) {
        t62 = <p>{t58}{" "}{t59}, {t60}{" "}{t61}</p>;
        $[197] = t59;
        $[198] = t61;
        $[199] = t62;
      } else {
        t62 = $[199];
      }
      if ($[200] !== t54 || $[201] !== t57 || $[202] !== t62) {
        t20 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t54}{t57}{t62}</div>;
        $[200] = t54;
        $[201] = t57;
        $[202] = t62;
        $[203] = t20;
      } else {
        t20 = $[203];
      }
      let t63;
      let t64;
      if ($[204] === Symbol.for("react.memo_cache_sentinel")) {
        t63 = <p className="font-semibold">Recommended Receiving Provider / Location</p>;
        t64 = <strong>Provider:</strong>;
        $[204] = t63;
        $[205] = t64;
      } else {
        t63 = $[204];
        t64 = $[205];
      }
      const t65 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
      let t66;
      if ($[206] !== t65) {
        t66 = <CopyableContent text={t65} />;
        $[206] = t65;
        $[207] = t66;
      } else {
        t66 = $[207];
      }
      let t67;
      if ($[208] === Symbol.for("react.memo_cache_sentinel")) {
        t67 = <strong>Location:</strong>;
        $[208] = t67;
      } else {
        t67 = $[208];
      }
      const t68 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
      let t69;
      if ($[209] !== t68) {
        t69 = <CopyableContent text={t68} />;
        $[209] = t68;
        $[210] = t69;
      } else {
        t69 = $[210];
      }
      let t70;
      if ($[211] !== t66 || $[212] !== t69) {
        t70 = <p>{t64} {t66},{" "}{t67} {t69}</p>;
        $[211] = t66;
        $[212] = t69;
        $[213] = t70;
      } else {
        t70 = $[213];
      }
      let t71;
      if ($[214] !== resolvedReceiving.routedToSscardio) {
        t71 = resolvedReceiving.routedToSscardio && <p className="text-xs">Routing: <strong>sscardio</strong></p>;
        $[214] = resolvedReceiving.routedToSscardio;
        $[215] = t71;
      } else {
        t71 = $[215];
      }
      let t72;
      if ($[216] !== resolvedReceiving.flags) {
        t72 = resolvedReceiving.flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{resolvedReceiving.flags.map(_temp30)}</ul>;
        $[216] = resolvedReceiving.flags;
        $[217] = t72;
      } else {
        t72 = $[217];
      }
      let t73;
      if ($[218] !== resolvedReceiving.trace) {
        t73 = resolvedReceiving.trace.length > 0 && <details className="text-xs"><summary>Why</summary><ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{resolvedReceiving.trace.map(_temp31)}</ul></details>;
        $[218] = resolvedReceiving.trace;
        $[219] = t73;
      } else {
        t73 = $[219];
      }
      if ($[220] !== t70 || $[221] !== t71 || $[222] !== t72 || $[223] !== t73) {
        t21 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t63}{t70}{t71}{t72}{t73}</div>;
        $[220] = t70;
        $[221] = t71;
        $[222] = t72;
        $[223] = t73;
        $[224] = t21;
      } else {
        t21 = $[224];
      }
      let t74;
      if ($[225] === Symbol.for("react.memo_cache_sentinel")) {
        t74 = <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>;
        $[225] = t74;
      } else {
        t74 = $[225];
      }
      let t75;
      if ($[226] !== documentId) {
        t75 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[226] = documentId;
        $[227] = t75;
      } else {
        t75 = $[227];
      }
      let t76;
      if ($[228] !== patientEhrId) {
        t76 = <li>Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} /></li>;
        $[228] = patientEhrId;
        $[229] = t76;
      } else {
        t76 = $[229];
      }
      let t77;
      if ($[230] === Symbol.for("react.memo_cache_sentinel")) {
        t77 = <li>Hit save and proceed to quickview.</li>;
        $[230] = t77;
      } else {
        t77 = $[230];
      }
      let t78;
      if ($[231] !== resolvedReceiving.providerLabel || $[232] !== resolvedReceiving.providerNpi || $[233] !== resolvedReceiving.usualProviderWriteSafe) {
        t78 = resolvedReceiving.usualProviderWriteSafe ? <ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={true} /> : "Leave unchanged";
        $[231] = resolvedReceiving.providerLabel;
        $[232] = resolvedReceiving.providerNpi;
        $[233] = resolvedReceiving.usualProviderWriteSafe;
        $[234] = t78;
      } else {
        t78 = $[234];
      }
      const t79 = resolvedReceiving.usualProviderWriteSafe ? "New patient \u2014 add the usual provider to the care team." : "Established patient \u2014 left unchanged to preserve continuity of care.";
      let t80;
      if ($[235] !== t79) {
        t80 = <p className="text-xs italic mt-1">{t79}</p>;
        $[235] = t79;
        $[236] = t80;
      } else {
        t80 = $[236];
      }
      let t81;
      if ($[237] !== t78 || $[238] !== t80) {
        t81 = <li>Usual Provider:{" "}{t78}{t80}</li>;
        $[237] = t78;
        $[238] = t80;
        $[239] = t81;
      } else {
        t81 = $[239];
      }
      let t82;
      if ($[240] !== referral.referringProvider) {
        t82 = <ProviderCopyFields provider={referral.referringProvider} />;
        $[240] = referral.referringProvider;
        $[241] = t82;
      } else {
        t82 = $[241];
      }
      let t83;
      if ($[242] !== referringProviderSpecialty) {
        t83 = <CopyableContent text={referringProviderSpecialty} />;
        $[242] = referringProviderSpecialty;
        $[243] = t83;
      } else {
        t83 = $[243];
      }
      let t84;
      if ($[244] !== t82 || $[245] !== t83) {
        t84 = <li>Referring Provider: {t82}, Specialty:{" "}{t83}</li>;
        $[244] = t82;
        $[245] = t83;
        $[246] = t84;
      } else {
        t84 = $[246];
      }
      let t85;
      if ($[247] !== isPrimaryCareProvider || $[248] !== referral.referringProvider) {
        t85 = isPrimaryCareProvider && <li>PCP: <ProviderCopyFields provider={referral.referringProvider} /></li>;
        $[247] = isPrimaryCareProvider;
        $[248] = referral.referringProvider;
        $[249] = t85;
      } else {
        t85 = $[249];
      }
      let t86;
      if ($[250] !== referral.referringProvider || $[251] !== referringLocationAddress) {
        t86 = referral.referringProvider && <li>Referring provider address: <CopyableContent text={referringLocationAddress} /></li>;
        $[250] = referral.referringProvider;
        $[251] = referringLocationAddress;
        $[252] = t86;
      } else {
        t86 = $[252];
      }
      let t87;
      if ($[253] !== t81 || $[254] !== t84 || $[255] !== t85 || $[256] !== t86) {
        t87 = <li>Check the Patient Care Team:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t81}{t84}{t85}{t86}</ul></li>;
        $[253] = t81;
        $[254] = t84;
        $[255] = t85;
        $[256] = t86;
        $[257] = t87;
      } else {
        t87 = $[257];
      }
      let t88;
      if ($[258] !== emergencyContactName) {
        t88 = <li>Name: <CopyableContent text={emergencyContactName} /></li>;
        $[258] = emergencyContactName;
        $[259] = t88;
      } else {
        t88 = $[259];
      }
      let t89;
      if ($[260] !== emergencyContactPhoneNumber) {
        t89 = <li>Phone Number: <CopyableContent text={emergencyContactPhoneNumber} /></li>;
        $[260] = emergencyContactPhoneNumber;
        $[261] = t89;
      } else {
        t89 = $[261];
      }
      let t90;
      if ($[262] !== emergencyContactRelationship) {
        t90 = <li>Relationship to Patient: <CopyableContent text={emergencyContactRelationship} /></li>;
        $[262] = emergencyContactRelationship;
        $[263] = t90;
      } else {
        t90 = $[263];
      }
      let t91;
      if ($[264] !== t88 || $[265] !== t89 || $[266] !== t90) {
        t91 = <li>Check the Emergency Contact<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t88}{t89}{t90}</ul></li>;
        $[264] = t88;
        $[265] = t89;
        $[266] = t90;
        $[267] = t91;
      } else {
        t91 = $[267];
      }
      const t92 = primaryCoverageSummary ?? "N/A";
      let t93;
      if ($[268] !== t92) {
        t93 = <li>Primary: <CopyableContent text={t92} /></li>;
        $[268] = t92;
        $[269] = t93;
      } else {
        t93 = $[269];
      }
      let t94;
      if ($[270] !== secondaryCoverageSummary) {
        t94 = secondaryCoverageSummary && <li>Secondary: <CopyableContent text={secondaryCoverageSummary} /></li>;
        $[270] = secondaryCoverageSummary;
        $[271] = t94;
      } else {
        t94 = $[271];
      }
      let t95;
      let t96;
      if ($[272] !== insuranceStatusDisplay) {
        t95 = <CopyableContent text={insuranceStatusDisplay} />;
        t96 = insuranceStatusDisplay === "Missing Insurance" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[272] = insuranceStatusDisplay;
        $[273] = t95;
        $[274] = t96;
      } else {
        t95 = $[273];
        t96 = $[274];
      }
      let t97;
      if ($[275] !== insuranceStatusDisplay) {
        t97 = insuranceStatusDisplay === "Unverified" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[275] = insuranceStatusDisplay;
        $[276] = t97;
      } else {
        t97 = $[276];
      }
      let t98;
      if ($[277] !== t95 || $[278] !== t96 || $[279] !== t97) {
        t98 = <li>Eligibility check result: {t95}{t96}{t97}</li>;
        $[277] = t95;
        $[278] = t96;
        $[279] = t97;
        $[280] = t98;
      } else {
        t98 = $[280];
      }
      let t99;
      if ($[281] !== t93 || $[282] !== t94 || $[283] !== t98) {
        t99 = <li>Check the patient insurance<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t93}{t94}{t98}</ul></li>;
        $[281] = t93;
        $[282] = t94;
        $[283] = t98;
        $[284] = t99;
      } else {
        t99 = $[284];
      }
      let t100;
      if ($[285] === Symbol.for("react.memo_cache_sentinel")) {
        t100 = <li>Call and text are YES</li>;
        $[285] = t100;
      } else {
        t100 = $[285];
      }
      let t101;
      if ($[286] !== privacyNoticeOnFile) {
        t101 = privacyNoticeOnFile !== true && <p className="text-xs italic mt-1">{privacyNoticeOnFile === false ? "Privacy is not set on the Athena chart. Set it to YES during review." : "Could not verify Athena privacy. Confirm it is YES during review."}</p>;
        $[286] = privacyNoticeOnFile;
        $[287] = t101;
      } else {
        t101 = $[287];
      }
      let t102;
      if ($[288] !== t101) {
        t102 = <li>Privacy is YES{t101}</li>;
        $[288] = t101;
        $[289] = t102;
      } else {
        t102 = $[289];
      }
      let t103;
      if ($[290] === Symbol.for("react.memo_cache_sentinel")) {
        t103 = <li>If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value unchanged.</li>;
        $[290] = t103;
      } else {
        t103 = $[290];
      }
      let t104;
      if ($[291] !== t102) {
        t104 = <li>Go to full registration and verify:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t100}{t102}{t103}</ul></li>;
        $[291] = t102;
        $[292] = t104;
      } else {
        t104 = $[292];
      }
      if ($[293] !== t104 || $[294] !== t75 || $[295] !== t76 || $[296] !== t87 || $[297] !== t91 || $[298] !== t99) {
        t22 = <div>{t74}<ol className="list-decimal list-inside space-y-3">{t75}{t76}{t77}{t87}{t91}{t99}{t104}</ol></div>;
        $[293] = t104;
        $[294] = t75;
        $[295] = t76;
        $[296] = t87;
        $[297] = t91;
        $[298] = t99;
        $[299] = t22;
      } else {
        t22 = $[299];
      }
      if ($[300] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>;
        $[300] = t15;
      } else {
        t15 = $[300];
      }
      t11 = "list-decimal list-inside space-y-3";
      if ($[301] !== documentId) {
        t12 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[301] = documentId;
        $[302] = t12;
      } else {
        t12 = $[302];
      }
      let t105;
      if ($[303] !== referringProviderName) {
        t105 = <CopyableContent text={referringProviderName} />;
        $[303] = referringProviderName;
        $[304] = t105;
      } else {
        t105 = $[304];
      }
      const t106 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      let t107;
      if ($[305] !== t106) {
        t107 = <CopyableContent text={t106} />;
        $[305] = t106;
        $[306] = t107;
      } else {
        t107 = $[306];
      }
      let t108;
      if ($[307] !== referringLocationAddress) {
        t108 = <CopyableContent text={referringLocationAddress} />;
        $[307] = referringLocationAddress;
        $[308] = t108;
      } else {
        t108 = $[308];
      }
      if ($[309] !== t105 || $[310] !== t107 || $[311] !== t108) {
        t13 = <li>Referring provider: Name: {t105}, NPI:{" "}{t107}, Address:{" "}{t108}</li>;
        $[309] = t105;
        $[310] = t107;
        $[311] = t108;
        $[312] = t13;
      } else {
        t13 = $[312];
      }
      if ($[313] !== resolvedReceiving.providerLabel || $[314] !== resolvedReceiving.providerNpi || $[315] !== resolvedReceiving.providerWriteSafe) {
        t14 = <li>Receiving provider:{" "}<ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={resolvedReceiving.providerWriteSafe} /></li>;
        $[313] = resolvedReceiving.providerLabel;
        $[314] = resolvedReceiving.providerNpi;
        $[315] = resolvedReceiving.providerWriteSafe;
        $[316] = t14;
      } else {
        t14 = $[316];
      }
      t9 = "Patient Type: ";
      if ($[317] !== patientLabel) {
        t10 = <CopyableContent text={patientLabel} />;
        $[317] = patientLabel;
        $[318] = t10;
      } else {
        t10 = $[318];
      }
      t7 = "ml-4 mt-1 space-y-1 list-disc list-inside text-xs";
      t8 = sortedPastAppointments.length === 0 ? <li>No appointment history found</li> : sortedPastAppointments.map(_temp32);
    }
    $[12] = actionNote;
    $[13] = athenaData?.coverages;
    $[14] = athenaData?.patientDetails;
    $[15] = bucketLabel;
    $[16] = coverageReadback;
    $[17] = dateOfReferral;
    $[18] = decisionTrace;
    $[19] = documentId;
    $[20] = documentUploadLabels;
    $[21] = eligibilityDisplay;
    $[22] = emergencyContactName;
    $[23] = emergencyContactPhoneNumber;
    $[24] = emergencyContactRelationship;
    $[25] = followThroughEligible;
    $[26] = insuranceRequirements;
    $[27] = internalNoteValue;
    $[28] = isPrimaryCareProvider;
    $[29] = pastAppointments;
    $[30] = patient;
    $[31] = patientEhrId;
    $[32] = patientLabel;
    $[33] = pendingApproval;
    $[34] = primaryCareProviderAlreadyPresent;
    $[35] = primaryCoverageSummary;
    $[36] = privacyNoticeOnFile;
    $[37] = receivingAssignmentConfirmed;
    $[38] = receivingProviderName;
    $[39] = referral.diagnosisList;
    $[40] = referral.id;
    $[41] = referral.primaryCoverage;
    $[42] = referral.reasonForReferral;
    $[43] = referral.receivingLocation?.name;
    $[44] = referral.receivingProvider;
    $[45] = referral.referringLocation?.name;
    $[46] = referral.referringProvider;
    $[47] = referralAuthorization;
    $[48] = referringLocationAddress;
    $[49] = referringProviderName;
    $[50] = referringProviderSpecialty;
    $[51] = resolvedReceiving;
    $[52] = secondaryCoverageSummary;
    $[53] = task;
    $[54] = taskSteps;
    $[55] = unresolvedUpcomingProviders;
    $[56] = upcomingAppointmentProviderSummary;
    $[57] = upcomingAppointments;
    $[58] = view;
    $[59] = T0;
    $[60] = dataSourceEntries;
    $[61] = hasMissingInformation;
    $[62] = missingFields;
    $[63] = t10;
    $[64] = t11;
    $[65] = t12;
    $[66] = t13;
    $[67] = t14;
    $[68] = t15;
    $[69] = t16;
    $[70] = t17;
    $[71] = t18;
    $[72] = t19;
    $[73] = t20;
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
    $[77] = t24;
    $[78] = t25;
    $[79] = t7;
    $[80] = t8;
    $[81] = t9;
  } else {
    T0 = $[59];
    dataSourceEntries = $[60];
    hasMissingInformation = $[61];
    missingFields = $[62];
    t10 = $[63];
    t11 = $[64];
    t12 = $[65];
    t13 = $[66];
    t14 = $[67];
    t15 = $[68];
    t16 = $[69];
    t17 = $[70];
    t18 = $[71];
    t19 = $[72];
    t20 = $[73];
    t21 = $[74];
    t22 = $[75];
    t23 = $[76];
    t24 = $[77];
    t25 = $[78];
    t7 = $[79];
    t8 = $[80];
    t9 = $[81];
  }
  if (t25 !== Symbol.for("react.early_return_sentinel")) {
    return t25;
  }
  let t26;
  if ($[319] !== t7 || $[320] !== t8) {
    t26 = <ul className={t7}>{t8}</ul>;
    $[319] = t7;
    $[320] = t8;
    $[321] = t26;
  } else {
    t26 = $[321];
  }
  let t27;
  if ($[322] !== t10 || $[323] !== t26 || $[324] !== t9) {
    t27 = <li>{t9}{t10}{t26}</li>;
    $[322] = t10;
    $[323] = t26;
    $[324] = t9;
    $[325] = t27;
  } else {
    t27 = $[325];
  }
  let t28;
  if ($[326] !== dateOfReferral) {
    t28 = <li>Date: <CopyableContent text={dateOfReferral} /></li>;
    $[326] = dateOfReferral;
    $[327] = t28;
  } else {
    t28 = $[327];
  }
  let t29;
  if ($[328] !== internalNoteValue) {
    t29 = <CopyableContent text={internalNoteValue} />;
    $[328] = internalNoteValue;
    $[329] = t29;
  } else {
    t29 = $[329];
  }
  let t30;
  if ($[330] !== flags) {
    t30 = flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{flags.map(_temp33)}</ul>;
    $[330] = flags;
    $[331] = t30;
  } else {
    t30 = $[331];
  }
  let t31;
  if ($[332] !== t29 || $[333] !== t30) {
    t31 = <li>Internal Note: {t29}{t30}</li>;
    $[332] = t29;
    $[333] = t30;
    $[334] = t31;
  } else {
    t31 = $[334];
  }
  let t32;
  if ($[335] !== schedulingPriority) {
    t32 = <li>Urgency: <CopyableContent text={schedulingPriority} /></li>;
    $[335] = schedulingPriority;
    $[336] = t32;
  } else {
    t32 = $[336];
  }
  let t33;
  if ($[337] !== bucketLabel) {
    t33 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <>{NO_CONFIDENT_BUCKET_SUGGESTION}</> : <>Suggested Bucket: <CopyableContent text={SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} /></>;
    $[337] = bucketLabel;
    $[338] = t33;
  } else {
    t33 = $[338];
  }
  let t34;
  if ($[339] !== bucketLabel || $[340] !== decisionTrace) {
    t34 = decisionTrace.length > 0 && (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{decisionTrace.map(_temp34)}</ul> : <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs"><li>{decisionTrace[decisionTrace.length - 1].label}{" "}<strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong></li></ul>);
    $[339] = bucketLabel;
    $[340] = decisionTrace;
    $[341] = t34;
  } else {
    t34 = $[341];
  }
  let t35;
  if ($[342] !== t33 || $[343] !== t34) {
    t35 = <li>{t33}{t34}</li>;
    $[342] = t33;
    $[343] = t34;
    $[344] = t35;
  } else {
    t35 = $[344];
  }
  let t36;
  if ($[345] !== actionNote) {
    t36 = <li>Action note: <CopyableContent text={actionNote} /></li>;
    $[345] = actionNote;
    $[346] = t36;
  } else {
    t36 = $[346];
  }
  let t37;
  if ($[347] !== internalNoteValue) {
    t37 = <li>Go to quickview and add the internal note: <CopyableContent text={internalNoteValue} /></li>;
    $[347] = internalNoteValue;
    $[348] = t37;
  } else {
    t37 = $[348];
  }
  let t38;
  if ($[349] !== t11 || $[350] !== t12 || $[351] !== t13 || $[352] !== t14 || $[353] !== t27 || $[354] !== t28 || $[355] !== t31 || $[356] !== t32 || $[357] !== t35 || $[358] !== t36 || $[359] !== t37) {
    t38 = <ol className={t11}>{t12}{t13}{t14}{t27}{t28}{t31}{t32}{t35}{t36}{t37}</ol>;
    $[349] = t11;
    $[350] = t12;
    $[351] = t13;
    $[352] = t14;
    $[353] = t27;
    $[354] = t28;
    $[355] = t31;
    $[356] = t32;
    $[357] = t35;
    $[358] = t36;
    $[359] = t37;
    $[360] = t38;
  } else {
    t38 = $[360];
  }
  let t39;
  if ($[361] !== t15 || $[362] !== t38) {
    t39 = <div>{t15}{t38}</div>;
    $[361] = t15;
    $[362] = t38;
    $[363] = t39;
  } else {
    t39 = $[363];
  }
  let t40;
  let t41;
  if ($[364] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>;
    t41 = <p className="mb-1">Verify the following documents were uploaded:</p>;
    $[364] = t40;
    $[365] = t41;
  } else {
    t40 = $[364];
    t41 = $[365];
  }
  let t42;
  if ($[366] !== documentUploadLabels) {
    t42 = documentUploadLabels.length === 0 ? <li>N/A</li> : documentUploadLabels.map(_temp35);
    $[366] = documentUploadLabels;
    $[367] = t42;
  } else {
    t42 = $[367];
  }
  let t43;
  if ($[368] !== t42) {
    t43 = <div>{t40}{t41}<ul className="ml-4 space-y-1 list-disc list-inside">{t42}</ul></div>;
    $[368] = t42;
    $[369] = t43;
  } else {
    t43 = $[369];
  }
  let t44;
  let t45;
  if ($[370] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>;
    t45 = <p className="font-medium text-foreground">Step 1: Missing Information Check</p>;
    $[370] = t44;
    $[371] = t45;
  } else {
    t44 = $[370];
    t45 = $[371];
  }
  let t46;
  if ($[372] !== hasMissingInformation || $[373] !== missingFields) {
    t46 = hasMissingInformation ? `Missing: ${missingFields.map(_temp36).join(", ")}` : "No missing-information letter needed.";
    $[372] = hasMissingInformation;
    $[373] = missingFields;
    $[374] = t46;
  } else {
    t46 = $[374];
  }
  let t47;
  if ($[375] !== t46) {
    t47 = <p className="mb-3">{t46}</p>;
    $[375] = t46;
    $[376] = t47;
  } else {
    t47 = $[376];
  }
  let t48;
  if ($[377] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>;
    $[377] = t48;
  } else {
    t48 = $[377];
  }
  let t49;
  if ($[378] !== dataSourceEntries) {
    t49 = <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">{dataSourceEntries.map(_temp37)}</ul>;
    $[378] = dataSourceEntries;
    $[379] = t49;
  } else {
    t49 = $[379];
  }
  let t50;
  if ($[380] !== dateOfReferral || $[381] !== hasMissingInformation || $[382] !== referringProviderName) {
    t50 = hasMissingInformation && <ol className="list-decimal list-inside space-y-2"><li>Verify the Auto-Generated document was added to the patient chart</li><li>Send the document out.<ul className="ml-4 mt-1 space-y-2 list-none"><li>a. Chart Export - sending to referring provider<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Click the hamburger menu (3 dashes) → select Chart Export.</li><li>Click the + button next to Admin Documents.</li><li>Find the "Request for Additional Information" doc and move it to the right-hand side.</li><li>Click Export Chart Sections → send to <CopyableContent text={referringProviderName} /></li></ul></li><li>c. Fill out export details<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Type <CopyableContent text={referringProviderName} /></li><li>Confirm method is set to Athena Fax and the fax number has populated.</li><li>Attention box: <CopyableContent text={referringProviderName} /></li><li>Note field: "Request for missing information."</li><li>Confirm Submit, then Save.</li></ul></li><li>d. Add Note to Patient Chart<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>"Valerie Health: Missing Information Requested <CopyableContent text={dateOfReferral} />"</li></ul></li></ul></li></ol>;
    $[380] = dateOfReferral;
    $[381] = hasMissingInformation;
    $[382] = referringProviderName;
    $[383] = t50;
  } else {
    t50 = $[383];
  }
  let t51;
  if ($[384] !== t47 || $[385] !== t49 || $[386] !== t50) {
    t51 = <div>{t44}{t45}{t47}{t48}{t49}{t50}</div>;
    $[384] = t47;
    $[385] = t49;
    $[386] = t50;
    $[387] = t51;
  } else {
    t51 = $[387];
  }
  let t52;
  if ($[388] !== t16 || $[389] !== t17 || $[390] !== t18 || $[391] !== t19 || $[392] !== t20 || $[393] !== t21 || $[394] !== t22 || $[395] !== t39 || $[396] !== t43 || $[397] !== t51) {
    t52 = <div className={t16}>{t17}{t18}{t19}{t20}{t21}{t22}{t39}{t43}{t51}</div>;
    $[388] = t16;
    $[389] = t17;
    $[390] = t18;
    $[391] = t19;
    $[392] = t20;
    $[393] = t21;
    $[394] = t22;
    $[395] = t39;
    $[396] = t43;
    $[397] = t51;
    $[398] = t52;
  } else {
    t52 = $[398];
  }
  let t53;
  if ($[399] !== T0 || $[400] !== t23 || $[401] !== t52) {
    t53 = <T0 collapsibleTitle={t23}>{t52}</T0>;
    $[399] = T0;
    $[400] = t23;
    $[401] = t52;
    $[402] = t53;
  } else {
    t53 = $[402];
  }
  let t54;
  if ($[403] !== referral.id || $[404] !== task.id) {
    t54 = <ReferralEligibilityCard referralId={referral.id} taskId={task.id} />;
    $[403] = referral.id;
    $[404] = task.id;
    $[405] = t54;
  } else {
    t54 = $[405];
  }
  let t55;
  if ($[406] !== athenaData || $[407] !== referral) {
    t55 = <AthenaReferralCards referral={referral} athenaData={athenaData} />;
    $[406] = athenaData;
    $[407] = referral;
    $[408] = t55;
  } else {
    t55 = $[408];
  }
  let t56;
  if ($[409] !== t24 || $[410] !== t53 || $[411] !== t54 || $[412] !== t55) {
    t56 = <div className={t24}>{t53}{t54}{t55}</div>;
    $[409] = t24;
    $[410] = t53;
    $[411] = t54;
    $[412] = t55;
    $[413] = t56;
  } else {
    t56 = $[413];
  }
  return t56;
}
function _temp37(entry_2) {
  return <li key={entry_2.label}>{entry_2.label}: <CopyableContent text={entry_2.value} /> ({entry_2.source})</li>;
}
function _temp36(entry_1) {
  return entry_1.label;
}
function _temp35(label) {
  return <li key={label}><CopyableContent text={label} /></li>;
}
function _temp34(entry_0, index_3) {
  return <li key={`${entry_0.label}-${index_3}`}>{entry_0.label} <strong>{entry_0.outcome}</strong></li>;
}
function _temp33(flag_0, index_2) {
  return <li key={index_2}>{flag_0}</li>;
}
function _temp32(appointment) {
  return <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>;
}
function _temp31(entry, index_1) {
  return <li key={index_1}>{entry}</li>;
}
function _temp30(flag, index_0) {
  return <li key={index_0}>{flag}</li>;
}
function _temp29(value_0, index, values) {
  return values.indexOf(value_0) === index;
}
function _temp28(value) {
  return Boolean(value);
}
function _temp27(item) {
  return item.diagnosisText;
}
function _temp26(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}